import React, { useState, useEffect, useMemo } from 'react';
import QRCode from 'qrcode';
import { salesAPI, customerAPI, itemAPI, masterAPI, inventoryAPI, supplierAPI, damageAPI, settingsAPI } from '../services/api';
import DataTable from '../components/ui/DataTable';
import SearchableSelect from '../components/ui/SearchableSelect';
import toast from 'react-hot-toast';
import { Plus, TrendingUp, X, Save, Trash2, PlusCircle, AlertCircle, CheckCircle, Edit2, Printer, ChevronDown, ChevronUp, Camera, FileSpreadsheet } from 'lucide-react';

const fmt2 = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt3 = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const today = () => new Date().toISOString().split('T')[0];

// ── Seller (Carat Quest / Shivora International LLP) ──────────

const SIGNATURE_BASE64 = 'data:image/png;base64,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';
const SELLER = {
  brand: 'CARAT QUEST',
  company: 'SHIVORA INTERNATIONAL LLP',
  street: '206 Krink Tower, Mini Bazaar, Varachha',
  cityStateZip: 'Surat, GJ 395006',
  address: '206 Krink Tower, Mini Bazaar, Varachha, Surat 395006',
  email: 'hello@thecaratquest.com',
  website: 'www.thecaratquest.com',
  gstin: '24AFWFS9858R1ZD',
  pan: 'AFWFS9858R',
  contact: '+91 63560 39856',
};

// ── Number → Indian words (for "Amount in Words") ─────────────
const ONES = ['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE', 'TEN',
  'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN', 'SEVENTEEN', 'EIGHTEEN', 'NINETEEN'];
const TENS = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];
const twoDigitWords = (n) => n < 20 ? ONES[n] : TENS[Math.floor(n / 10)] + (n % 10 ? ' ' + ONES[n % 10] : '');
const threeDigitWords = (n) => {
  let s = '';
  if (n >= 100) { s += ONES[Math.floor(n / 100)] + ' HUNDRED'; n %= 100; if (n) s += ' '; }
  if (n) s += twoDigitWords(n);
  return s;
};
const amountInWords = (amount) => {
  let n = Math.round(Number(amount) || 0);
  if (n === 0) return 'ZERO RUPEES ONLY';
  const crore = Math.floor(n / 10000000); n %= 10000000;
  const lakh = Math.floor(n / 100000); n %= 100000;
  const thou = Math.floor(n / 1000); n %= 1000;
  const parts = [];
  if (crore) parts.push(threeDigitWords(crore) + ' CRORE');
  if (lakh) parts.push(threeDigitWords(lakh) + ' LAKH');
  if (thou) parts.push(threeDigitWords(thou) + ' THOUSAND');
  if (n) parts.push(threeDigitWords(n));
  return parts.join(' ') + ' RUPEES ONLY';
};

// ── Print the tax invoice — professional redesign with bank details & QR ──
const printSalesInvoice = async (row) => {
  let header = null, details = [], bankSettings = {};
  try {
    const [invoiceRes, settingsRes] = await Promise.all([
      salesAPI.getById(row.salesID),
      settingsAPI.get().catch(() => ({ data: { data: null } })),
    ]);
    header = invoiceRes.data.data?.header || null;
    details = invoiceRes.data.data?.details || [];
    const sd = settingsRes.data.data;
    if (sd) bankSettings = {
      bankName: sd.bankname || '',
      accountHolder: sd.accountholder || '',
      accountNo: sd.accountno || '',
      ifscCode: sd.ifsccode || '',
      upiid: sd.upiid || '',
      bankBranch: sd.bankbranch || '',
      terms: sd.terms || '',
    };
  } catch { toast.error('Failed to load invoice for printing'); return; }
  if (!header) { toast.error('Invoice not found'); return; }

  const addrParts = [header.customerAddress, header.customerCity, header.customerState, header.customerPinCode]
    .filter(Boolean).join(', ');

  const isIGST = Number(header.igstAmount || 0) > 0 || details.some(d => Number(d.igstAmount || 0) > 0 || Number(d.igstRate || d.igstrate || 0) > 0 || d.isIGST === true);

  const totalItemDiscount = details.reduce((sum, d) => {
    const qty = Number(d.quantity) || 1;
    const disc = Number(d.discount || 0);
    return sum + (disc * qty > 0 ? disc * qty : disc);
  }, 0);

  const invoiceDiscount = Number(header.discount || 0) > 0 ? Number(header.discount) : totalItemDiscount;

  const rawPhone = header.customerMobile || header.customermobile || header.mobile || header.phone || row.customerMobile || row.customermobile || row.mobile || '';
  const cleanMobile = String(rawPhone).replace(/\D/g, '');
  let whatsappPhone = cleanMobile.length === 10 ? `91${cleanMobile}` : cleanMobile.length > 10 ? cleanMobile : '';

  const shareText = `✨ *Welcome to CARAT QUEST!* ✨\n\nDear *${header.customerName || 'Customer'}*,\n\nThank you for shopping with us! 🙏\n\n📄 *Invoice No:* ${header.invoiceNo}\n📅 *Date:* ${new Date(header.invoiceDate).toLocaleDateString('en-IN')}\n💰 *Grand Total:* ₹${fmt2(header.totalAmount)}\n\nBest Regards,\n*Carat Quest Team*`;
  const encodedText = encodeURIComponent(shareText);
  const whatsappUrl = whatsappPhone
    ? `https://wa.me/${whatsappPhone}?text=${encodedText}`
    : `https://wa.me/?text=${encodedText}`;

  // Show bank details & QR code on ALL invoices regardless of payment mode
  const showBank = true;
  const hasBankDetails = bankSettings.accountNo || bankSettings.bankName || bankSettings.accountHolder;
  const bankSection = hasBankDetails ? `
    <div class="bank-card">
      <div class="bank-head">Bank Details</div>
      <table class="bank-tbl">
        ${bankSettings.accountHolder ? `<tr><td class="lbl">Name</td><td class="val">: ${bankSettings.accountHolder}</td></tr>` : ''}
        ${bankSettings.ifscCode ? `<tr><td class="lbl">IFSC</td><td class="val">: ${bankSettings.ifscCode}</td></tr>` : ''}
        ${bankSettings.accountNo ? `<tr><td class="lbl">Account No</td><td class="val">: ${bankSettings.accountNo}</td></tr>` : ''}
        ${bankSettings.bankName ? `<tr><td class="lbl">Bank Name</td><td class="val">: ${bankSettings.bankName}${bankSettings.bankBranch ? `, ${bankSettings.bankBranch}` : ''}</td></tr>` : ''}
      </table>
    </div>` : '';

  // UPI QR code string & embedded base64 image
  const upiStr = bankSettings.upiid
    ? `upi://pay?pa=${encodeURIComponent(bankSettings.upiid)}&pn=${encodeURIComponent(bankSettings.accountHolder || 'Carat Quest')}&am=${Number(header.totalAmount).toFixed(2)}&cu=INR`
    : '';

  let qrDataUrl = '';
  if (upiStr) {
    try {
      qrDataUrl = await QRCode.toDataURL(upiStr, { width: 180, margin: 1 });
    } catch {
      qrDataUrl = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(upiStr)}`;
    }
  }

  const termsText = bankSettings.terms ? String(bankSettings.terms).trim() : '';
  const termsLines = termsText ? termsText.split('\n').filter(Boolean).map((t, i) => `<div>${i + 1}. ${t}</div>`).join('') : '';

  // Determine GST rate percentages for header
  let headerIgstPct = 3, headerCgstPct = 1.5, headerSgstPct = 1.5;
  for (const d of details) {
    const cR = Number(d.cgstRate || d.cgstrate || 0);
    const sR = Number(d.sgstRate || d.sgstrate || 0);
    const iR = Number(d.igstRate || d.igstrate || d.gstPercent || d.gstpercent || (cR + sR) || 0);
    if (iR > 0) {
      headerIgstPct = iR;
      headerCgstPct = cR || iR / 2;
      headerSgstPct = sR || iR / 2;
      break;
    }
  }
  if (headerIgstPct === 3 && details.length > 0) {
    const totTaxable = details.reduce((s, d) => s + Number(d.taxableAmount || 0), 0);
    const totIgst = details.reduce((s, d) => s + (Number(d.igstAmount || 0) || (Number(d.totalAmount || 0) - Number(d.taxableAmount || 0))), 0);
    if (totTaxable > 0 && totIgst > 0) {
      const calc = Math.round((totIgst / totTaxable) * 100);
      if (calc > 0) {
        headerIgstPct = calc;
        headerCgstPct = calc / 2;
        headerSgstPct = calc / 2;
      }
    }
  }

  const html = `<!DOCTYPE html><html><head><title>Invoice ${header.invoiceNo}</title>
  <meta name="viewport" content="width=800, initial-scale=0.5, maximum-scale=2.0" />
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    html { overflow-x: auto; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      color: #1e293b;
      padding: 5px;
      font-size: 11px;
      background: #fff;
    }

    .page-frame {
      border: 3.5px double #000000;
      padding: 12px;
      border-radius: 4px;
      position: relative;
      background: #fff;
      max-width: 750px;
      width: 100%;
      margin: 0 auto;
      box-sizing: border-box;
    }

    .toolbar { text-align: center; margin-bottom: 14px; gap: 10px; display: flex; justify-content: center; }
    .toolbar button { padding: 8px 24px; font-size: 14px; font-weight: 600; cursor: pointer; border: none; border-radius: 6px; background: #0f172a; color: #fff; }
    @media print {
      .toolbar { display: none !important; }
      body { padding: 0; margin: 0; }
      .page-frame { border: 3px double #000000; padding: 10px; width: 100%; max-width: 100%; }
      @page { size: A4; margin: 4mm; }
    }

    /* ── Header ── */
    .head { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 10px; margin-bottom: 8px; }
    .company { display: flex; align-items: center; gap: 14px; }
    .companyLogo { height: 56px; width: auto; object-fit: contain; border-radius: 6px; }
    .companyInfo .brand { font-weight: 900; font-size: 17px; color: #1e3a5f; letter-spacing: .02em; line-height: 1.2; }
    .companyInfo .cname { font-weight: 700; font-size: 11px; color: #475569; margin-top: 2px; }
    .companyInfo .cline { color: #64748b; font-size: 9.5px; margin-top: 2px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .inv-title { text-align: right; }
    .inv-title h2 { font-size: 20px; font-weight: 900; color: #1e3a5f; letter-spacing: .08em; }
    .inv-badge { display: inline-block; border: 1px solid #cbd5e1; font-size: 8.5px; font-weight: 700; color: #64748b; padding: 2px 8px; border-radius: 4px; text-transform: uppercase; margin-top: 4px; letter-spacing: .04em; }

    /* ── Meta horizontal bar ── */
    .meta-bar {
      display: flex;
      justify-content: space-between;
      border-top: 1.5px solid #111827;
      border-bottom: 1.5px solid #111827;
      padding: 6px 10px;
      margin-bottom: 10px;
      background: #fff;
    }
    .meta-item { display: flex; flex-direction: column; gap: 1px; }
    .meta-lbl { font-size: 9px; font-weight: 800; color: #1e3a5f; text-transform: uppercase; letter-spacing: .04em; }
    .meta-val { font-size: 11px; font-weight: 700; color: #0f172a; }

    /* ── Bill To ── */
    .bill-to-box { margin-bottom: 12px; padding: 4px 6px; }
    .bill-to-title { font-size: 11px; font-weight: 800; color: #1e3a5f; margin-bottom: 3px; }
    .bill-to-name { font-size: 12px; font-weight: 700; color: #0f172a; }
    .bill-to-sub { font-size: 10px; color: #475569; margin-top: 2px; }

    /* ── Items table (Clean white with dark black borders) ── */
    .table-responsive { width: 100%; overflow-x: hidden; margin-bottom: 12px; }
    .table-responsive::-webkit-scrollbar { display: none; }
    table.items { width: 100%; border-collapse: collapse; table-layout: fixed; }
    table.items th, table.items td { border: 1px solid #111827; padding: 4px 3px; font-size: 8.5px; word-wrap: break-word; overflow-wrap: break-word; }
    table.items th.r, table.items td.r, table.items th.c, table.items td.c { white-space: nowrap; word-wrap: normal; overflow-wrap: normal; }
    table.items th { background: #fff; color: #1e3a5f; font-weight: 800; text-transform: uppercase; font-size: 8.5px; letter-spacing: .02em; text-align: left; border-top: 1.5px solid #111827; border-bottom: 1.5px solid #111827; }
    table.items th.c, table.items td.c { text-align: center; }
    table.items th.r, table.items td.r { text-align: right; font-family: 'Courier New', monospace; }
    table.items td.b { font-weight: 700; color: #0f172a; }
    table.items tbody tr:nth-child(even) { background: #fff; }
    table.items tr.subtotal-row td { border-top: 1.5px solid #111827; border-bottom: 1.5px solid #111827; font-size: 8.5px; font-weight: 800; padding: 4px 2px; font-family: inherit; }

    /* ── Bottom split section ── */
    .bottom-container { display: flex; gap: 16px; margin-bottom: 10px; align-items: stretch; }
    .bottom-col-left { flex: 1.1; display: flex; flex-direction: column; justify-content: flex-start; gap: 8px; }
    .bottom-col-right { flex: 1; display: flex; flex-direction: column; justify-content: space-between; gap: 8px; }

    /* Terms */
    .terms-card { padding: 4px 0; }
    .terms-head { font-size: 10px; font-weight: 800; color: #1e3a5f; margin-bottom: 4px; }
    .terms-body { font-size: 9px; color: #475569; line-height: 1.5; }

    /* Bank Details Card — styled with matching black border */
    .bank-card { border: 1.5px solid #111827; border-radius: 10px; padding: 8px 12px; background: #fff; }
    .bank-head { font-size: 11px; font-weight: 800; color: #1e3a5f; margin-bottom: 4px; }
    .bank-tbl { width: 100%; border-collapse: collapse; }
    .bank-tbl td { font-size: 10px; padding: 1.5px 0; color: #334155; }
    .bank-tbl td.lbl { font-weight: 700; color: #1e3a5f; width: 85px; }
    .bank-tbl td.val { color: #0f172a; font-weight: 600; }

    /* Payment QR Code Box — matched black border */
    .qr-card { border: 1.5px solid #111827; border-radius: 10px; padding: 8px 12px; display: flex; align-items: center; gap: 14px; background: #fff; }
    .qr-img-wrap { border: 1px solid #10b981; border-radius: 6px; padding: 3px; background: #fff; flex-shrink: 0; }
    .qr-info { flex: 1; min-width: 0; }
    .qr-title { font-size: 11px; font-weight: 800; color: #1e3a5f; margin-bottom: 4px; }
    .qr-badges { display: flex; align-items: center; gap: 10px; margin-bottom: 6px; flex-wrap: wrap; }
    .pay-logo { display: inline-flex; align-items: center; gap: 3px; }
    .qr-upi-id { font-size: 9.5px; color: #475569; font-weight: 700; word-break: break-all; }

    /* Right column summary tables */
    .summary-tbl { width: 100%; border-collapse: collapse; }
    .summary-tbl td { padding: 3px 0; font-size: 10.5px; color: #334155; }
    .summary-tbl td.r { text-align: right; font-family: 'Courier New', monospace; font-weight: 600; color: #0f172a; }
    .summary-tbl tr.total-row td { font-size: 13px; font-weight: 900; color: #1e3a5f; padding: 6px 0; border-top: 1.5px solid #111827; border-bottom: 1.5px solid #111827; }

    /* Total Amount in Words */
    .words-card { margin-top: 2px; }
    .words-title { font-size: 10px; font-weight: 800; color: #1e3a5f; margin-bottom: 2px; }
    .words-val { font-size: 10.5px; font-weight: 600; color: #334155; }

    /* Authorized Signature box — black border */
    .sign-card { border: none; padding: 8px 12px; text-align: center; background: transparent; }
    .sign-img { height: 42px; width: auto; max-width: 140px; object-fit: contain; }
    .sign-lbl { font-weight: 800; font-size: 10.5px; color: #1e3a5f; margin-top: 2px; }
    .sign-sub { font-size: 9px; color: #64748b; font-weight: 600; }

    /* Footer */
    .inv-footer { text-align: center; margin-top: 10px; padding-top: 6px; border-top: 1px solid #e2e8f0; font-size: 9.5px; color: #64748b; }

    /* 📱 MOBILE RESPONSIVE VIEW ── */
    @media (max-width: 680px) {
      body { padding: 4px; font-size: 10px; }
      .page-frame { padding: 8px; border: 2px solid #000000; border-radius: 4px; }
      .head { display: flex; flex-direction: row; justify-content: space-between; align-items: flex-start; gap: 6px; }
      .companyLogo { height: 40px; }
      .companyInfo .brand { font-size: 14px; }
      .inv-title h2 { font-size: 15px; }
      .meta-bar { display: flex; flex-direction: row; justify-content: space-between; padding: 4px 6px; gap: 4px; }
      .meta-lbl { font-size: 8px; }
      .meta-val { font-size: 9.5px; }
      .table-responsive { overflow-x: auto; -webkit-overflow-scrolling: touch; }
      table.items { min-width: 600px; }
      table.items th, table.items td { padding: 3px 2px; font-size: 8px; }
      .bottom-container { flex-direction: column; gap: 10px; }
      .bottom-col-left, .bottom-col-right { width: 100%; }
    }
  </style></head><body>
    <div class="toolbar">
      <button onclick="window.print()">🖨️ Print Invoice</button>
      <button onclick="shareWhatsAppWithPDF()" style="background:#25D366;">💬 Share on WhatsApp</button>
    </div>

    <div class="page-frame">
      <!-- Header -->
      <div class="head">
        <div class="company">
          <img src="/carat-quest-logo.png" alt="Logo" class="companyLogo" />
          <div class="companyInfo">
            <div class="brand">${SELLER.brand}</div>
            <div class="cname">${SELLER.company}</div>
            <div class="cline">
              <span>📞 ${SELLER.contact}</span>
              <span>📧 ${SELLER.email}</span>
            </div>
            <div class="cline">📍 ${SELLER.address}</div>
          </div>
        </div>
        <div class="inv-title">
          <h2>TAX INVOICE</h2>
          <div class="inv-badge">Original for Recipient</div>
          <div style="font-size:9.5px;color:#64748b;margin-top:4px;"><b>GSTIN:</b> ${SELLER.gstin}</div>
        </div>
      </div>

      <!-- Meta horizontal bar (Anand Solar style) -->
      <div class="meta-bar">
        <div class="meta-item">
          <span class="meta-lbl">Invoice No.</span>
          <span class="meta-val">${header.invoiceNo}</span>
        </div>
        <div class="meta-item">
          <span class="meta-lbl">Invoice Date</span>
          <span class="meta-val">${new Date(header.invoiceDate).toLocaleDateString('en-IN')}</span>
        </div>
        <div class="meta-item">
          <span class="meta-lbl">Payment Mode</span>
          <span class="meta-val">${header.paymentMode || 'ONLINE/UPI'}</span>
        </div>
      </div>

      <!-- Bill To -->
      <div class="bill-to-box">
        <div class="bill-to-title">Bill To</div>
        <div class="bill-to-name">${header.customerName}</div>
        <div class="bill-to-sub"><b>Mobile:</b> ${header.customerMobile || '—'} &nbsp;|&nbsp; <b>Address:</b> ${header.billingAddress || addrParts || '—'}</div>
      </div>

      <!-- Items table (Beige Header & Subtotal) -->
      <div class="table-responsive">
        <table class="items">
          <thead><tr>
            <th class="c" style="width:4%">SR.<br/>NO.</th>
            <th class="c" style="width:14%">SKU</th>
            <th class="c" style="width:14%">Description</th>
            <th class="c" style="width:5%">HSN</th>
            <th class="c" style="width:4%">QTY</th>
            <th class="r" style="width:8.5%">MRP</th>
            <th class="r" style="width:7.5%">Discount</th>
            <th class="r" style="width:9%">Sale Price</th>
            <th class="r" style="width:9%">Taxable</th>
            ${isIGST ? `
              <th class="r" style="width:13%">IGST<br/>(${headerIgstPct}%)</th>
            ` : `
              <th class="r" style="width:6.5%">CGST<br/>(${headerCgstPct}%)</th>
              <th class="r" style="width:6.5%">SGST<br/>(${headerSgstPct}%)</th>
            `}
            <th class="r" style="width:12%">Total (₹)</th>
          </tr></thead>
          <tbody>
            ${details.map((d, i) => {
    const qty = Number(d.quantity) || 1;
    const mrp = d.mrp != null ? Number(d.mrp) : (Number(d.totalAmount) / qty);
    const discount = Number(d.discount || 0);
    const salePrice = Number(d.totalAmount);
    const taxable = Number(d.taxableAmount || 0) || (salePrice / 1.03);
    const lineGst = salePrice - taxable;
    const halfGst = lineGst / 2;
    const cgstAmt = Number(d.cgstAmount || 0) || (!isIGST ? halfGst : 0);
    const sgstAmt = Number(d.sgstAmount || 0) || (!isIGST ? halfGst : 0);
    const igstAmt = Number(d.igstAmount || 0) || (isIGST ? lineGst : 0);
    const hsnCode = d.hsnCode || d.hsn || '—';

    const taxTd = isIGST ? `
                <td class="r">${fmt2(igstAmt)}</td>
              ` : `
                <td class="r">${fmt2(cgstAmt)}</td>
                <td class="r">${fmt2(sgstAmt)}</td>
              `;

    return `
                <tr>
                  <td class="c">${i + 1}</td>
                  <td class="c" style="font-size:8px;font-weight:700;word-break:break-all;">${d.skuNo || d.itemCode || '—'}</td>
                  <td class="c">${d.itemName}${d.purityCode ? ` (${d.purityCode})` : ''}</td>
                  <td class="c">${hsnCode}</td>
                  <td class="c">${qty}</td>
                  <td class="r">${fmt2(mrp)}</td>
                  <td class="r">${fmt2(discount)}</td>
                  <td class="r">${fmt2(salePrice)}</td>
                  <td class="r">${fmt2(taxable)}</td>
                  ${taxTd}
                  <td class="r b">₹${fmt2(d.totalAmount)}</td>
                </tr>`;
  }).join('')}
            ${(() => {
    const totalSalePriceSum = details.reduce((s, d) => s + Number(d.totalAmount || 0), 0);
    const totalTaxableSum = details.reduce((s, d) => s + (Number(d.taxableAmount || 0) || (Number(d.totalAmount || 0) / 1.03)), 0);
    const totalCgstSum = Number(header.cgstAmount || 0) || details.reduce((s, d) => {
      const tax = Number(d.taxableAmount || 0) || (Number(d.totalAmount || 0) / 1.03);
      return s + (Number(d.cgstAmount || 0) || ((Number(d.totalAmount || 0) - tax) / 2));
    }, 0);
    const totalSgstSum = Number(header.sgstAmount || 0) || details.reduce((s, d) => {
      const tax = Number(d.taxableAmount || 0) || (Number(d.totalAmount || 0) / 1.03);
      return s + (Number(d.sgstAmount || 0) || ((Number(d.totalAmount || 0) - tax) / 2));
    }, 0);
    const totalIgstSum = Number(header.igstAmount || 0) || details.reduce((s, d) => {
      const tax = Number(d.taxableAmount || 0) || (Number(d.totalAmount || 0) / 1.03);
      return s + (Number(d.igstAmount || 0) || (Number(d.totalAmount || 0) - tax));
    }, 0);

    return `
            <tr class="subtotal-row">
              <td colspan="4">SUBTOTAL</td>
              <td class="c">${details.reduce((s, d) => s + (Number(d.quantity) || 1), 0)}</td>
              <td></td>
              <td class="r">₹${fmt2(details.reduce((s, d) => s + Number(d.discount || 0), 0))}</td>
              <td class="r">₹${fmt2(totalSalePriceSum)}</td>
              <td class="r">₹${fmt2(totalTaxableSum)}</td>
              ${isIGST ? `
                <td class="r">₹${fmt2(totalIgstSum)}</td>
              ` : `
                <td class="r">₹${fmt2(totalCgstSum)}</td>
                <td class="r">₹${fmt2(totalSgstSum)}</td>
              `}
              <td class="r">₹${fmt2(totalSalePriceSum)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Bottom split section — Matched 100% to reference image -->
      <div class="bottom-container">
        <!-- Left Column: Terms, Bank Details & Payment QR Code -->
        <div class="bottom-col-left">
          ${termsLines ? `
          <div class="terms-card">
            <div class="terms-head">Terms &amp; Conditions</div>
            <div class="terms-body">${termsLines}</div>
          </div>` : ''}

          ${(showBank && bankSection) ? bankSection : ''}

          ${(showBank && qrDataUrl) ? `
          <div class="qr-card">
            <div class="qr-img-wrap">
              <img src="${qrDataUrl}" width="84" height="84" alt="Payment QR Code" style="display:block;" />
            </div>
            <div class="qr-info">
              <div class="qr-title">Payment QR Code</div>
              <div class="qr-badges">
                <span class="pay-logo">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="12" fill="#5f259f"/><text x="12" y="16.5" fill="#fff" font-size="14" font-weight="900" text-anchor="middle" font-family="Arial, sans-serif">पे</text></svg>
                  <span style="color:#5f259f;font-weight:700;font-size:10px;">PhonePe</span>
                </span>
                <span class="pay-logo">
                  <svg width="14" height="14" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" fill="#EA4335"/></svg>
                  <span style="color:#5f6368;font-weight:600;font-size:10px;">Pay</span>
                </span>
                <span class="pay-logo" style="font-weight:900;font-size:11px;">
                  <span style="color:#002e6e;">pay</span><span style="color:#00baf2;">tm</span>
                </span>
                <span class="pay-logo" style="font-style:italic;font-weight:900;font-size:11px;color:#555;">
                  <span>UPI</span>
                  <svg width="14" height="11" viewBox="0 0 16 14" fill="none"><path d="M2 14L8 7L2 0H6.5L12.5 7L6.5 14H2Z" fill="#008822"/><path d="M7.5 14L13.5 7L7.5 0H10L16 7L10 14H7.5Z" fill="#FF9900"/></svg>
                </span>
              </div>
              <div class="qr-upi-id"><b>UPI ID:</b> ${bankSettings.upiid}</div>
            </div>
          </div>` : ''}
        </div>

        <!-- Right Column: Totals, Amount in Words & Signature Box -->
        <div class="bottom-col-right">
          <table class="summary-tbl">
            <tr><td>Taxable Amount</td><td class="r">₹ ${fmt2(header.taxableAmount || totalTaxableSum)}</td></tr>
            <tr><td>Discount</td><td class="r">${invoiceDiscount > 0 ? `₹ ${fmt2(invoiceDiscount)}` : `₹ 0.00`}</td></tr>
            ${isIGST
      ? `<tr><td>IGST</td><td class="r">₹ ${fmt2(totalIgstSum)}</td></tr>`
      : `<tr><td>CGST</td><td class="r">₹ ${fmt2(totalCgstSum)}</td></tr>
                 <tr><td>SGST</td><td class="r">₹ ${fmt2(totalSgstSum)}</td></tr>`}
            ${(() => {
              const explicitShip = Number(header.shippingCharges ?? header.shippingcharges ?? header.shipping ?? header.shippingFee ?? header.courierCharges ?? row.shippingCharges ?? row.shippingcharges ?? 0);
              return `<tr><td>Shipping Charges</td><td class="r">₹ ${fmt2(explicitShip)}</td></tr>`;
            })()}
            <tr class="total-row"><td>Total Amount</td><td class="r">₹ ${fmt2(header.totalAmount)}</td></tr>
          </table>`;
  })()}

          <div class="words-card">
            <div class="words-title">Total Amount (in words)</div>
            <div class="words-val">${amountInWords(header.totalAmount)}</div>
          </div>

          <div class="sign-card">
            <img src="${SIGNATURE_BASE64}" class="sign-img" alt="Authorized Signature" />
            <div class="sign-lbl">Signature</div>
            <div class="sign-sub">${SELLER.company}</div>
          </div>


        </div>
      </div>

      <div class="inv-footer">
        <div>Thank you for shopping with Carat Quest!</div>
        <div style="margin-top: 3px; font-weight: 600; color: #475569;">${SELLER.website}</div>
      </div>
    </div>

    <script src="https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js"></script>
    <script>
      async function shareWhatsAppWithPDF() {
        const toolbar = document.querySelector('.toolbar');
        if (toolbar) toolbar.style.display = 'none';

        // Safe single-quote embedded values (phone=digits only, text=URL-encoded)
        const waPhone = '${whatsappPhone}';
        const waEncoded = '${encodedText}';
        const waText = decodeURIComponent(waEncoded);

        function buildWaUrl(ph) {
          return ph ? ('https://wa.me/' + ph + '?text=' + waEncoded)
                    : ('https://wa.me/?text=' + waEncoded);
        }

        const canNativeShare = !!(navigator.share && navigator.canShare);

        if (!canNativeShare) {
          let url = buildWaUrl(waPhone);
          if (!waPhone) {
            const entered = prompt('Enter customer WhatsApp number (10 digits):');
            if (entered) {
              const d = entered.replace(/\D/g,'');
              url = buildWaUrl((d.length===10?'91':'')+d);
            }
          }
          (window.opener || window).open(url, '_blank');
        }

        const opt = {
          margin: [5, 5, 5, 5],
          filename: 'Invoice_${header.invoiceNo.replace(/\//g, '_')}.pdf',
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, logging: false },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        };

        try {
          const frameEl = document.querySelector('.page-frame') || document.body;
          const pdfBlob = await html2pdf().from(frameEl).set(opt).output('blob');
          const pdfFile = new File([pdfBlob], opt.filename, { type: 'application/pdf' });
          if (canNativeShare && navigator.canShare({ files: [pdfFile] })) {
            await navigator.share({ title: opt.filename, text: waText, files: [pdfFile] });
          } else {
            const link = document.createElement('a');
            link.href = URL.createObjectURL(pdfBlob);
            link.download = opt.filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        } catch(e) {
          console.warn('Share error:', e);
          html2pdf().from(document.body).set(opt).save();
        } finally {
          if (toolbar) toolbar.style.display = 'flex';
        }
      }
    <\/script>
  </body></html>`;

  const w = window.open('', '_blank', 'width=1050,height=850');
  if (!w) return;
  w.document.write(html);
  w.document.close();
};

// ── Excel-style dropdown options ──────────────────────────────
const CHANNELS = ['WEBSITE', 'INSTAGRAM', 'FACEBOOK', 'WHATSAPP', 'MARKETPLACE', 'STORE', 'EXHIBITION', 'OTHER'];
const PAY_METHODS = ['ONLINE/UPI', 'CASH', 'CARD', 'COD', 'BANK', 'NEFT', 'CHEQUE'];
const PAY_STATUSES = ['RECEIVED', 'PENDING', 'PARTIAL'];
const ORDER_STATUSES = ['PROCESSING', 'PACKED', 'SHIPPED', 'DELIVERED', 'RETURNED', 'CANCELLED'];
const COURIERS = ['SHIPROCKET', 'DELHIVERY', 'BLUEDART', 'DTDC', 'INDIA POST', 'EKART', 'HAND DELIVERY'];

const EMPTY_LINE = {
  itemID: '', skuKey: '', skuNo: '', quantity: 1, weight: '', sellingPrice: '',
  unitWeight: '', unitPrice: '',   // per-piece values from the purchase form — scaled by qty
  mrp: '', discount: 0,            // per-piece Rate/Discount — printed on the tax invoice
  gstPercent: 3, isIGST: false
};

// Selling price is GST-inclusive (Excel: 5000 → taxable 4854.37 + GST 145.63)
const lineCalc = (d) => {
  const sp = Number(d.sellingPrice || 0);
  const g = Number(d.gstPercent || 0);
  const taxable = sp > 0 ? sp / (1 + g / 100) : 0;
  const gstAmt = sp - taxable;
  return { sp, taxable, gstAmt, half: gstAmt / 2 };
};

// ── Validation ────────────────────────────────────────────────
const validateHeader = (h) => {
  const e = {};
  if (!h.invoiceDate) e.invoiceDate = 'Date is required';
  else if (h.invoiceDate > today()) e.invoiceDate = 'Date cannot be in the future';
  if (!h.branchID) e.branchID = 'Select a branch';
  if (!h.customerID) e.customerID = 'Select a customer';
  if (Number(h.discount) < 0) e.discount = 'Discount cannot be negative';
  if (h.paymentStatus === 'PARTIAL' && Number(h.paidAmount) <= 0)
    e.paidAmount = 'Enter the amount received';
  return e;
};

// availableQtyMap: itemID -> qty currently in stock (see stockMap/originalQtyByItem
// in SalesPage). Requested qty is summed per item across all lines first, so two
// lines for the same item can't each pass individually while together overselling.
const validateDetails = (details, availableQtyMap = {}) => {
  const totalByItem = {};
  details.forEach(d => {
    if (d.itemID) totalByItem[d.itemID] = (totalByItem[d.itemID] || 0) + (Number(d.quantity) || 0);
  });
  return details.map(d => {
    const e = {};
    if (!d.itemID) e.itemID = 'Select SKU / item';
    if (!d.weight || Number(d.weight) <= 0) e.weight = 'Enter weight > 0';
    if (!d.sellingPrice || Number(d.sellingPrice) <= 0) e.sellingPrice = 'Enter selling price > 0';
    if (Number(d.quantity) <= 0) {
      e.quantity = 'Qty must be > 0';
    } else if (d.itemID) {
      // Items sold down to zero/negative balance don't appear in the
      // current-stock list at all (it only returns items with a nonzero
      // ledger balance) — treating a missing entry as "unlimited" instead
      // of 0 is exactly how an item can get oversold further, so default
      // to 0 rather than skipping the check.
      const avail = availableQtyMap[d.itemID] != null ? availableQtyMap[d.itemID] : 0;
      if (totalByItem[d.itemID] > avail) e.quantity = `Only ${avail} in stock`;
    }
    if (Number(d.gstPercent) < 0 || Number(d.gstPercent) > 28) e.gstPercent = 'GST must be 0–28%';
    return e;
  });
};

// ── Number input — no spinners ────────────────────────────────
function NumInput({ value, onChange, placeholder = '0', step = '0.01',
  min = '0', error = false, style = {} }) {
  return (
    <input
      type="number"
      value={value === undefined || value === null ? '' : value}
      onChange={e => {
        if (typeof onChange === 'function') {
          onChange(e);
        }
      }}
      placeholder={placeholder} step={step} min={min}
      style={{
        width: '100%', padding: '6px 6px',
        border: `1.5px solid ${error ? 'var(--red)' : 'var(--silver-300)'}`,
        borderRadius: 6, fontSize: 12.5,
        fontFamily: 'var(--mono)', textAlign: 'right',
        background: error ? '#fff5f5' : '#fff',
        color: 'var(--navy-800)', MozAppearance: 'textfield', ...style
      }}
      onFocus={e => e.target.style.borderColor = error ? 'var(--red)' : 'var(--navy-600)'}
      onBlur={e => e.target.style.borderColor = error ? 'var(--red)' : 'var(--silver-300)'}
    />
  );
}

// ── Inline error text ─────────────────────────────────────────
function Err({ msg }) {
  if (!msg) return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 4,
      marginTop: 4, fontSize: 11, color: 'var(--red)', fontWeight: 500
    }}>
      <AlertCircle size={11} />
      {msg}
    </div>
  );
}

// ── Field wrapper ─────────────────────────────────────────────
function Field({ label, error, required, children, style = {}, labelExtra }) {
  return (
    <div className="form-group" style={{ margin: 0, ...style }}>
      <label className="form-label" style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
        {label}{required && <span style={{ color: 'var(--red)', marginLeft: 2 }}>*</span>}{labelExtra}
      </label>
      {children}
      <Err msg={error} />
    </div>
  );
}

// Read-only auto-filled box (customer contact/city/state, taxable, GST)
const ROBox = ({ value, color = '#6b7280', bold = false }) => (
  <div style={{
    padding: '6px 8px', border: '1px dashed #d1d5db', borderRadius: 6, fontSize: 12,
    fontFamily: 'var(--mono)', textAlign: 'right', background: 'rgba(255,255,255,.65)',
    color, fontWeight: bold ? 700 : 500, whiteSpace: 'nowrap', overflow: 'hidden'
  }}>
    {value}
  </div>
);

export default function SalesPage() {
  const [data, setData] = useState([]);
  const [itemRows, setItemRows] = useState([]);   // per-line item view
  const [view, setView] = useState('invoices'); // 'invoices' | 'items'
  const [groupBy, setGroupBy] = useState('none');      // 'none' | 'invoice' | 'customer' | 'item'
  const [itemSearch, setItemSearch] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState({}); // { [groupKey]: true } when collapsed
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);   // false | 'new' | 'edit'
  const [editID, setEditID] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // { id, no }
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [skuList, setSkuList] = useState([]);   // SKUs from the purchase form
  const [branches, setBranches] = useState([]);
  const [stockMap, setStockMap] = useState({});   // itemID -> current stock qty (selected branch)
  // itemID -> qty already committed to THIS invoice before edits — added back to
  // stockMap so editing an invoice without changing qty doesn't false-positive as
  // "exceeds stock" (the update flow deletes + re-adds this invoice's own lines).
  const [originalQtyByItem, setOriginalQtyByItem] = useState({});
  const [showAddressSection, setShowAddressSection] = useState(false);

  // ── Tree view: per-invoice item details, fetched lazily on expand ──
  const [invItems, setInvItems] = useState({}); // salesID -> { loading, error, rows }
  const loadInvItems = async (row) => {
    const id = row.salesID;
    if (invItems[id]?.rows || invItems[id]?.loading) return;
    setInvItems(p => ({ ...p, [id]: { loading: true } }));
    try {
      const res = await salesAPI.getById(id);
      setInvItems(p => ({ ...p, [id]: { loading: false, rows: res.data?.data?.details || [] } }));
    } catch {
      setInvItems(p => ({ ...p, [id]: { loading: false, error: true, rows: [] } }));
    }
  };

  const invItemsPanel = (row) => {
    const st = invItems[row.salesID];
    if (!st || st.loading) return <div style={{ padding: 18, textAlign: 'center', color: 'var(--silver-500)' }}><span className="spinner" style={{ width: 18, height: 18 }} /></div>;
    if (st.error) return <div style={{ padding: 18, textAlign: 'center', color: 'var(--red)' }}>Failed to load items</div>;
    if (!st.rows.length) return <div style={{ padding: 18, textAlign: 'center', color: 'var(--silver-500)' }}>No items on this invoice</div>;
    const thS = { padding: '7px 12px', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--silver-500)', textAlign: 'left', whiteSpace: 'nowrap', borderBottom: '1px solid var(--silver-200, #e2e8f0)' };
    const thSR = { ...thS, textAlign: 'right' };
    const tdS = { padding: '7px 12px', fontSize: 12.5, whiteSpace: 'nowrap', borderBottom: '1px solid #eef2f7' };
    const tdSR = { ...tdS, textAlign: 'right', fontFamily: 'var(--mono)' };
    return (
      <div style={{ padding: '10px 16px 14px 48px' }}>
        <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid var(--silver-200, #e2e8f0)', borderRadius: 10 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={thS}>#</th><th style={thS}>SKU No</th><th style={thS}>Item</th>
              <th style={thSR}>Qty</th><th style={thSR}>Net Wt</th>
              <th style={thSR}>MRP</th><th style={thSR}>Discount</th><th style={thSR}>Selling ₹</th>
              <th style={thSR}>Taxable</th><th style={thSR}>GST ₹</th><th style={thSR}>Total</th>
            </tr></thead>
            <tbody>
              {st.rows.map((it, ix) => (
                <tr key={it.salesDetailID || ix}>
                  <td style={tdS}>{ix + 1}</td>
                  <td style={tdS}><code style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--primary)' }}>{it.skuNo || '—'}</code></td>
                  <td style={{ ...tdS, fontWeight: 600 }}>{it.itemName}{it.purityCode ? ` (${it.purityCode})` : ''}</td>
                  <td style={tdSR}>{Number(it.quantity || 0)}</td>
                  <td style={tdSR}>{fmt3(it.weight)} g</td>
                  <td style={tdSR}>{it.mrp ? `₹${fmt2(it.mrp)}` : '—'}</td>
                  <td style={{ ...tdSR, color: it.discount > 0 ? 'var(--red)' : undefined }}>{it.discount > 0 ? `₹${fmt2(it.discount)}` : '—'}</td>
                  <td style={{ ...tdSR, fontWeight: 700 }}>₹{fmt2(it.rate)}</td>
                  <td style={tdSR}>₹{fmt2(it.taxableAmount)}</td>
                  <td style={tdSR}>₹{fmt2(Number(it.cgstAmount || 0) + Number(it.sgstAmount || 0) + Number(it.igstAmount || 0))}</td>
                  <td style={{ ...tdSR, fontWeight: 700, color: 'var(--navy-700, #334155)' }}>₹{fmt2(it.totalAmount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ── Detailed Sales Excel export (one row per sold item) ──
  const exportSalesDetail = async () => {
    if (!itemRows.length) { toast.error('No sales data to export'); return; }
    const XLSX = await import('xlsx');
    const dstr = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '';
    const num = (n) => Number(n || 0);
    const rows = itemRows.map(r => ({
      'Date': dstr(r.invoiceDate),
      'Invoice No': r.invoiceNo || '',
      'SKU No': r.skuNo || '',
      'Customer Name': r.customerName || '',
      'Contact No': r.customerMobile || '',
      'City': r.customerCity || '',
      'State': r.customerState || '',
      'Sales Channel': r.salesChannel || '',
      'Item': r.itemName || '',
      'Qty': num(r.quantity),
      // Selling Price = per-unit amount charged (GST-inclusive)
      'Selling Price': num(r.quantity) > 0 ? Math.round((num(r.totalAmount) / num(r.quantity)) * 100) / 100 : num(r.totalAmount),
      'Taxable': num(r.taxableAmount),
      'GST%': num(r.gstPercent),
      'Total': num(r.totalAmount),
      'Payment Method': r.paymentMode || '',
      'Payment Status': r.paymentStatus || '',
      'Order Status': r.orderStatus || '',
      'Courier Partner': r.courierPartner || '',
      'Tracking No': r.trackingNo || '',
      'Notes': r.notes || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = Object.keys(rows[0]).map(k => ({ wch: Math.max(12, k.length + 3) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sales');
    XLSX.writeFile(wb, `sales-detail-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // ── Quick-add customer (the + next to the Customer Name label) ──
  const QUICK_CUST_EMPTY = { customerName: '', mobile: '', city: '', state: '' };
  const [quickAddCust, setQuickAddCust] = useState(false);
  const [quickCustForm, setQuickCustForm] = useState(QUICK_CUST_EMPTY);
  const [quickCustSaving, setQuickCustSaving] = useState(false);

  const handleQuickCustSave = async (e) => {
    e.preventDefault();
    if (!quickCustForm.customerName.trim()) { toast.error('Customer name is required'); return; }
    setQuickCustSaving(true);
    try {
      const res = await customerAPI.create(quickCustForm);
      const newID = res.data?.data?.customerID;
      const c = await customerAPI.getAll({ isActive: true });
      const list = c.data.data || [];
      setCustomers(list);
      if (newID) {
        const cust = list.find(x => x.customerID === newID);
        const addr = cust ? [cust.address, cust.city, cust.state, cust.pinCode].filter(Boolean).join(', ') : '';
        setHeader(h => ({
          ...h,
          customerID: newID,
          billingAddress: addr || h.billingAddress,
          shippingAddress: (h.sameAsBilling !== false) ? (addr || h.billingAddress) : h.shippingAddress
        }));
      }
      toast.success(`Customer "${quickCustForm.customerName}" added`);
      setQuickAddCust(false);
      setQuickCustForm(QUICK_CUST_EMPTY);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add customer');
    } finally { setQuickCustSaving(false); }
  };

  const EMPTY_HEADER = {
    invoiceDate: today(), branchID: '', customerID: '',
    salesChannel: 'WEBSITE',
    paymentMode: 'ONLINE/UPI', paymentStatus: 'RECEIVED',
    orderStatus: 'PROCESSING', courierPartner: '', trackingNo: '',
    billingAddress: '', shippingAddress: '', sameAsBilling: true,
    discount: 0, shippingCharges: 0, paidAmount: 0, remarks: ''
  };
  const [header, setHeader] = useState(EMPTY_HEADER);
  const [details, setDetails] = useState([{ ...EMPTY_LINE }]);

  // stockMap (current branch stock) with this invoice's own already-committed
  // quantities added back in — see openEdit / originalQtyByItem above.
  const availableQtyMap = { ...stockMap };
  Object.keys(originalQtyByItem).forEach(id => {
    availableQtyMap[id] = (availableQtyMap[id] || 0) + originalQtyByItem[id];
  });

  // Validation — only shown after first submit attempt
  const hErr = submitted ? validateHeader(header) : {};
  const dErrs = submitted ? validateDetails(details, availableQtyMap) : details.map(() => ({}));
  const isFormValid = Object.keys(hErr).length === 0 && dErrs.every(e => Object.keys(e).length === 0);

  const [suppliers, setSuppliers] = useState([]);
  const [damageList, setDamageList] = useState([]);
  const [damageModal, setDamageModal] = useState(false); // false | 'damage' | 'exchange'
  const [damageForm, setDamageForm] = useState({
    branchID: '', itemID: '', vendorSKUNo: '', supplierID: '',
    damageDate: today(), grossWeight: '', netWeight: '', labourPerGram: '',
    silverRatePerGram: '', purchasePrice: '', description: '', customerName: '', status: 'Damage',
    imageUrl: ''
  });
  const [editingDamageID, setEditingDamageID] = useState(null);
  const [savingDamage, setSavingDamage] = useState(false);

  const loadDamageData = async () => {
    try {
      const res = await damageAPI.getAll();
      setDamageList(res.data.data || []);
    } catch { setDamageList([]); }
  };

  const openEditDamage = (r, mode) => {
    setEditingDamageID(r.damageID);
    let custName = r.customerName || '';
    let desc = r.description || '';
    if (desc.startsWith('Party: ')) {
      const parts = desc.split(' | ');
      custName = parts[0].replace('Party: ', '');
      desc = parts.slice(1).join(' | ');
    }
    setDamageForm({
      branchID: r.branchID || branches[0]?.branchID || '',
      itemID: r.itemID || '',
      vendorSKUNo: r.vendorSKUNo || r.skuNo || '',
      supplierID: r.supplierID || '',
      damageDate: r.damageDate ? r.damageDate.split('T')[0] : today(),
      grossWeight: r.grossWeight || '',
      netWeight: r.netWeight || '',
      labourPerGram: r.labourPerGram || '',
      silverRatePerGram: r.silverRatePerGram || '',
      purchasePrice: r.purchasePrice || '',
      description: desc,
      customerName: custName,
      status: r.status || (mode === 'exchange' ? 'Exchange' : 'Damage'),
      imageUrl: r.imageUrl || ''
    });
    setDamageModal(mode);
  };

  const handleDeleteDamage = async (r) => {
    if (!window.confirm(`Are you sure you want to delete entry ${r.damageNo || r.damageID}?`)) return;
    try {
      await damageAPI.delete(r.damageID);
      toast.success('Entry deleted successfully!');
      loadDamageData();
    } catch {
      toast.error('Failed to delete entry');
    }
  };

  const handleSaveDamage = async (e) => {
    e.preventDefault();
    if (!damageForm.netWeight && !damageForm.grossWeight) {
      return toast.error('Please enter weight');
    }
    setSavingDamage(true);
    try {
      const descParts = [];
      if (damageForm.customerName) descParts.push(`Party: ${damageForm.customerName}`);
      if (damageForm.description) descParts.push(damageForm.description);

      const payload = {
        ...damageForm,
        branchID: Number(damageForm.branchID || branches[0]?.branchID || 1),
        itemID: damageForm.itemID ? Number(damageForm.itemID) : null,
        supplierID: damageForm.supplierID ? Number(damageForm.supplierID) : null,
        grossWeight: Number(damageForm.grossWeight || damageForm.netWeight || 0),
        netWeight: Number(damageForm.netWeight || damageForm.grossWeight || 0),
        labourPerGram: Number(damageForm.labourPerGram || 0),
        silverRatePerGram: Number(damageForm.silverRatePerGram || 0),
        purchasePrice: Number(damageForm.purchasePrice || 0),
        damageDate: damageForm.damageDate || today(),
        description: descParts.join(' | '),
        status: damageForm.status || (damageModal === 'exchange' ? 'Exchange' : 'Damage'),
      };

      if (editingDamageID) {
        await damageAPI.update(editingDamageID, payload);
        toast.success(damageModal === 'exchange' ? 'Exchange entry updated!' : 'Damage entry updated!');
      } else {
        await damageAPI.create(payload);
        toast.success(damageModal === 'exchange' ? 'Exchange entry added!' : 'Damage entry added!');
      }
      setDamageModal(false);
      setEditingDamageID(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save entry');
    } finally {
      setSavingDamage(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try { const res = await salesAPI.getAll(); setData(res.data.data || []); }
    catch { toast.error('Failed to load sales'); }
    finally { setLoading(false); }
    try { const v = await salesAPI.getItemView(); setItemRows(v.data.data || []); }
    catch { setItemRows([]); }
    try { const d = await damageAPI.getAll(); setDamageList(d.data.data || []); }
    catch { setDamageList([]); }
  };

  const loadStock = async (branchID) => {
    try {
      const r = await inventoryAPI.getCurrentStock(branchID ? { branchID } : undefined);
      const map = {};
      (r.data.data || []).forEach(s => { map[s.itemID] = (map[s.itemID] || 0) + Number(s.currentQty || 0); });
      setStockMap(map);
    } catch { setStockMap({}); }
  };

  const loadMasters = async () => {
    try {
      const [c, i, b, sup] = await Promise.all([
        customerAPI.getAll({ isActive: true }),
        itemAPI.getAll(),
        masterAPI.getBranches(),
        supplierAPI.getAll()
      ]);
      setCustomers(c.data.data || []);
      setItems(i.data.data || []);
      setBranches(b.data.data || []);
      setSuppliers(sup.data.data || []);
      if (b.data.data?.length) setHeader(h => ({ ...h, branchID: b.data.data[0].branchID }));
    } catch { }
    // SKUs entered in the purchase form (with last purchased weight / price for autofill)
    try {
      const s = await itemAPI.getSkuList();
      setSkuList(s.data.data || []);
    } catch { setSkuList([]); }
  };

  // Dropdown source: one entry per purchase invoice line when available, else item master
  // Only lines still IN STOCK are sellable — sold/other-status lines are hidden entirely
  // rather than just annotated, so the dropdown can't be used to oversell.
  const skuOptions = skuList.length > 0
    ? skuList
      .filter(s => !s.productStatus || s.productStatus === 'IN STOCK')
      .map(s => ({
        key: s.purchaseDetailID ? `pd${s.purchaseDetailID}` : `it${s.itemID}`,
        itemID: s.itemID,
        label: `${s.skuNo || s.itemCode} — ${s.itemName}`,
        skuNo: s.skuNo,
        gstPercent: s.gstPercent,
        defWeight: s.lastWeight || s.standardWeight || '',
        defPrice: s.lastSalePrice || s.lastMRP || '',
        supplierID: s.supplierID || null,
        supplierName: s.supplierName || null,
      }))
    : items.map(it => ({
      key: `it${it.itemID}`,
      itemID: it.itemID,
      label: `${it.itemCode ? `${it.itemCode} — ` : ''}${it.itemName} (${it.purityCode})`,
      skuNo: null,
      gstPercent: it.gstPercent,
      defWeight: it.standardWeight || '',
      defPrice: it.mrp > 0 ? it.mrp : '',
    }));

  useEffect(() => { load(); loadMasters(); }, []);
  // Re-check stock if the user switches branch mid-form (modal must be open —
  // openModal/openEdit already load stock for the initial branch).
  useEffect(() => { if (modal && header.branchID) loadStock(header.branchID); }, [header.branchID]); // eslint-disable-line

  const openModal = () => {
    setSubmitted(false);
    setEditID(null);
    const branchID = branches[0]?.branchID || '';
    setHeader({ ...EMPTY_HEADER, branchID });
    setDetails([{ ...EMPTY_LINE }]);
    setOriginalQtyByItem({});
    loadStock(branchID);
    setModal('new');
  };

  const openEdit = async (row) => {
    setSubmitted(false);
    setEditID(row.salesID);
    const isReceivedRow = row.status === 'PAID' || row.paymentStatus === 'RECEIVED' || Number(row.balanceAmount || 0) <= 0.50;
    const derivedPS = isReceivedRow ? 'RECEIVED' : (row.status === 'PARTIAL' ? 'PARTIAL' : 'PENDING');
    let head = {
      invoiceDate: row.invoiceDate?.split('T')[0] || today(),
      branchID: String(row.branchID || branches[0]?.branchID || ''),
      customerID: String(row.customerID || ''),
      salesChannel: row.salesChannel || 'STORE',
      paymentMode: row.paymentMode || 'ONLINE/UPI',
      paymentStatus: derivedPS,
      orderStatus: row.orderStatus || 'PENDING',
      courierPartner: row.courierPartner || '',
      trackingNo: row.trackingNo || '',
      discount: row.discount || 0,
      shippingCharges: row.shippingCharges || row.shipping || row.shippingFee || row.courierCharges || 0,
      paidAmount: isReceivedRow ? (row.totalAmount || 0) : (row.paidAmount || 0),
      remarks: row.remarks || '',
    };
    try {
      const res = await salesAPI.getById(row.salesID);
      const h = res.data.data?.header || {};
      const det = res.data.data?.details || [];
      const loadedPaid = h.paidAmount ?? h.paidamount ?? row.paidAmount ?? 0;
      const ps = (h.paymentStatus === 'RECEIVED' || h.status === 'PAID' || isReceivedRow) ? 'RECEIVED' : (h.paymentStatus || head.paymentStatus);
      const explicitShip = Number(h.shippingCharges ?? h.shippingcharges ?? row.shippingCharges ?? row.shippingcharges ?? 0);

      head = {
        ...head,
        salesChannel: h.saleschannel || h.salesChannel || head.salesChannel,
        paymentStatus: ps,
        paidAmount: ps === 'RECEIVED' ? (h.totalAmount || h.totalamount || row.totalAmount) : loadedPaid,
        orderStatus: h.orderstatus || h.orderStatus || row.orderStatus || 'PENDING',
        courierPartner: h.courierpartner || h.courierPartner || row.courierPartner || '',
        trackingNo: h.trackingno || h.trackingNo || row.trackingNo || '',
        shippingCharges: explicitShip,
        billingAddress: h.billingAddress || [h.customerAddress, h.customerCity, h.customerState, h.customerPinCode].filter(Boolean).join(', '),
        shippingAddress: h.shippingAddress || h.billingAddress || [h.customerAddress, h.customerCity, h.customerState, h.customerPinCode].filter(Boolean).join(', '),
        sameAsBilling: !h.shippingAddress || h.shippingAddress === h.billingAddress,
        remarks: h.remarks ?? head.remarks,
      };
      setDetails(det.length > 0 ? det.map(d => {
        const qty = Number(d.quantity) > 0 ? Number(d.quantity) : 1;
        const mrpDiscSell = Number(d.mrp || 0) > 0 ? (Number(d.mrp) - Number(d.discount || 0)) * qty : 0;
        const sell = Number(d.totalAmount) > 0
          ? Number(d.totalAmount)
          : (mrpDiscSell > 0 ? mrpDiscSell : ((Number(d.weight) * Number(d.rate)) + Number(d.makingCharges || 0)) * (1 + Number(d.gstPercent || 0) / 100));
        return {
          salesDetailID: d.salesDetailID || d.salesdetailid,
          itemID: String(d.itemID ?? d.itemid ?? d.itemId ?? ''),
          skuKey: '',
          skuNo: d.skuNo || d.skuno || '',
          quantity: d.quantity,
          weight: d.weight,
          sellingPrice: sell.toFixed(2),
          unitWeight: Number(d.weight) / qty,
          unitPrice: sell / qty,
          mrp: d.mrp != null ? Number(d.mrp).toFixed(2) : '',
          discount: d.discount != null ? Number(d.discount) : 0,
          gstPercent: d.gstPercent,
          isIGST: Boolean(
            d.isIGST || d.isigst ||
            Number(h.igstAmount || h.igstamount || row.igstAmount || 0) > 0 ||
            (Number(d.cgstAmount ?? d.cgstamount ?? 0) === 0 && Number(d.gstPercent || 0) > 0 && Number(d.totalAmount || 0) > Number(d.taxableAmount || 0))
          ),
        };
      }) : [{ ...EMPTY_LINE }]);
      // These lines' quantities are already deducted from current stock (they
      // belong to this invoice) — add them back so re-saving unchanged doesn't
      // read as "exceeds stock".
      const orig = {};
      det.forEach(d => {
        const id = d.itemID ?? d.itemid ?? d.itemId;
        if (id) orig[id] = (orig[id] || 0) + (Number(d.quantity) || 0);
      });
      setOriginalQtyByItem(orig);
    } catch { setDetails([{ ...EMPTY_LINE }]); toast.error('Failed to load details'); setOriginalQtyByItem({}); }
    setHeader(head);
    loadStock(head.branchID);
    setModal('edit');
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await salesAPI.delete(confirmDelete.id);
      toast.success(`Invoice ${confirmDelete.no} deleted`);
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  const setH = (key, val) => setHeader(h => ({ ...h, [key]: val }));
  const addLine = () => setDetails(d => [...d, { ...EMPTY_LINE }]);
  const removeLine = (i) => setDetails(d => d.filter((_, idx) => idx !== i));
  const updateLine = (i, key, val) =>
    setDetails(d => d.map((r, idx) => idx === i ? { ...r, [key]: val } : r));

  // SKU picked → per-unit weight/price come from the purchase line, scaled by qty
  const onItemSelect = (i, key) => {
    const opt = skuOptions.find(o => o.key === key);
    setDetails(d => d.map((r, idx) => {
      if (idx !== i) return r;
      const qty = Number(r.quantity) > 0 ? Number(r.quantity) : 1;
      const uW = Number(opt?.defWeight || 0);
      const uP = Number(opt?.defPrice || 0);
      return {
        ...r,
        skuKey: key,
        skuNo: opt?.skuNo || '',
        itemID: opt ? String(opt.itemID) : '',
        gstPercent: opt?.gstPercent ?? r.gstPercent,
        unitWeight: uW > 0 ? uW : r.unitWeight,
        unitPrice: uP > 0 ? uP : r.unitPrice,
        weight: uW > 0 ? (uW * qty).toFixed(3) : r.weight,
        sellingPrice: uP > 0 ? (uP * qty).toFixed(2) : r.sellingPrice,
        mrp: uP > 0 ? uP.toFixed(2) : r.mrp,
        discount: 0,
      };
    }));
  };

  // Qty changed → re-scale weight and selling price from the per-unit values
  const onQtyChange = (i, val) => {
    setDetails(d => d.map((r, idx) => {
      if (idx !== i) return r;
      const qty = Number(val);
      const uW = Number(r.unitWeight || 0);
      const uP = Number(r.unitPrice || 0);
      return {
        ...r,
        quantity: val,
        ...(qty > 0 && uW > 0 ? { weight: (uW * qty).toFixed(3) } : {}),
        ...(qty > 0 && uP > 0 ? { sellingPrice: (uP * qty).toFixed(2) } : {}),
      };
    }));
  };

  // Manual weight / price edits redefine the per-unit values for the current qty
  const onWeightChange = (i, val) => {
    setDetails(d => d.map((r, idx) => idx === i ? {
      ...r, weight: val,
      unitWeight: Number(r.quantity) > 0 ? Number(val) / Number(r.quantity) : Number(val),
    } : r));
  };
  const onPriceChange = (i, val) => {
    setDetails(d => d.map((r, idx) => idx === i ? {
      ...r, sellingPrice: val,
      unitPrice: Number(r.quantity) > 0 ? Number(val) / Number(r.quantity) : Number(val),
    } : r));
  };

  // MRP / Discount are per-piece (Rate/Discount on the printed invoice) — auto-derive
  // the per-piece selling price so the qty-scaled Selling ₹ stays in sync
  const onMrpChange = (i, val) => {
    setDetails(d => d.map((r, idx) => {
      if (idx !== i) return r;
      const qty = Number(r.quantity) > 0 ? Number(r.quantity) : 1;
      const uP = Math.max(0, Number(val || 0) - Number(r.discount || 0));
      return { ...r, mrp: val, unitPrice: uP, sellingPrice: uP > 0 ? (uP * qty).toFixed(2) : r.sellingPrice };
    }));
  };
  const onDiscountChange = (i, val) => {
    setDetails(d => d.map((r, idx) => {
      if (idx !== i) return r;
      const qty = Number(r.quantity) > 0 ? Number(r.quantity) : 1;
      const uP = Math.max(0, Number(r.mrp || 0) - Number(val || 0));
      return { ...r, discount: val, unitPrice: uP, sellingPrice: uP > 0 ? (uP * qty).toFixed(2) : r.sellingPrice };
    }));
  };

  const selCustomer = customers.find(c => String(c.customerID) === String(header.customerID));

  const calcTotals = () => {
    let gross = 0;
    details.forEach(d => {
      gross += Number(d.sellingPrice || 0);
    });
    const ship = Number(header.shippingCharges || 0);
    const disc = Number(header.discount || 0);
    const netGross = Math.max(0, gross - disc);
    const discFactor = gross > 0 ? netGross / gross : 1;

    let taxable = 0, cgst = 0, sgst = 0, igst = 0;
    details.forEach(d => {
      const sp = Number(d.sellingPrice || 0);
      const g = Number(d.gstPercent || 0);
      const lineNetSp = sp * discFactor;
      const lineTaxable = lineNetSp > 0 ? lineNetSp / (1 + g / 100) : 0;
      const lineGst = lineNetSp - lineTaxable;

      taxable += lineTaxable;
      if (d.isIGST) {
        igst += lineGst;
      } else {
        cgst += lineGst / 2;
        sgst += lineGst / 2;
      }
    });

    const total = netGross + ship;
    return { taxable, cgst, sgst, igst, gross, ship, total };
  };

  const totals = calcTotals();
  const effectivePaid = (header.paymentStatus === 'RECEIVED' || header.paymentStatus === 'PAID')
    ? totals.total
    : header.paymentStatus === 'PENDING'
      ? 0
      : (Math.abs(totals.total - Number(header.paidAmount || 0)) <= 0.50 ? totals.total : Number(header.paidAmount || 0));
  const balance = Math.max(0, totals.total - effectivePaid);

  const handleSave = async (e) => {
    e.preventDefault();
    setSubmitted(true);

    const hE = validateHeader(header);
    const dE = validateDetails(details, availableQtyMap);
    if (Object.keys(hE).length > 0 || dE.some(e2 => Object.keys(e2).length > 0)) {
      const allErrors = [...Object.values(hE), ...dE.flatMap(e2 => Object.values(e2))];
      toast.error(allErrors[0]);
      return;
    }
    if (effectivePaid > totals.total) {
      toast.error(`Paid amount (₹${fmt2(effectivePaid)}) cannot exceed total (₹${fmt2(totals.total)})`);
      return;
    }
    if (Number(header.discount) >= totals.gross && totals.gross > 0) {
      toast.error('Discount cannot be equal or greater than the gross amount');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        invoiceDate: header.invoiceDate,
        branchID: +header.branchID,
        customerID: +header.customerID,
        discount: +header.discount,
        shippingCharges: +header.shippingCharges || 0,
        paidAmount: effectivePaid,
        paymentMode: header.paymentMode,
        remarks: header.remarks,
        salesChannel: header.salesChannel,
        paymentStatus: header.paymentStatus,
        orderStatus: header.orderStatus,
        courierPartner: header.courierPartner || null,
        trackingNo: header.trackingNo || null,
        details: details.map(d => {
          const c = lineCalc(d);
          return {
            salesDetailID: d.salesDetailID || null,
            itemID: +d.itemID,
            quantity: +d.quantity,
            weight: +d.weight,
            // Backend computes taxable = weight × rate (+making); derive rate from GST-incl price
            rate: +(c.taxable / Number(d.weight)).toFixed(4),
            makingCharges: 0,
            gstPercent: +d.gstPercent,
            isIGST: d.isIGST,
            // Printable tax-invoice columns
            skuNo: d.skuNo || null,
            mrp: Number(d.mrp) > 0 ? +d.mrp : null,
            discount: Number(d.discount) > 0 ? +d.discount : null,
          };
        })
      };
      if (editID) {
        await salesAPI.update(editID, payload);
        toast.success('Sales invoice updated!');
      } else {
        await salesAPI.create(payload);
        toast.success('Sales invoice created!');
      }
      setModal(false);
      setSubmitted(false);
      setEditID(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || (editID ? 'Failed to update' : 'Failed to create'));
    } finally { setSaving(false); }
  };

  const statusBadge = (s) => {
    const map = { PAID: 'badge-green', RECEIVED: 'badge-green', PARTIAL: 'badge-amber', PENDING: 'badge-amber', UNPAID: 'badge-red' };
    return <span className={`badge ${map[s] || 'badge-navy'}`}>{s}</span>;
  };

  const headerErrCount = Object.keys(hErr).length;
  const detailErrCount = dErrs.reduce((sum, e) => sum + Object.keys(e).length, 0);

  // Derive a searchable skuList string per invoice from already-loaded expanded items
  const rowsWithSku = useMemo(() => {
    const skuMap = new Map();
    Object.entries(invItems).forEach(([id, st]) => {
      if (st?.rows) {
        const hint = st.rows.map(it => it.skuNo).filter(Boolean).join(' ');
        skuMap.set(Number(id), hint);
        skuMap.set(String(id), hint);
      }
    });
    return data.map(r => ({
      ...r,
      _skuHints: skuMap.get(r.salesID) || '',
    }));
  }, [data, invItems]);

  const handleOrderStatusQuickChange = async (salesID, newStatus) => {
    try {
      await salesAPI.updateOrderStatus(salesID, newStatus);
      setData(prev => prev.map(item => item.salesID === salesID ? { ...item, orderStatus: newStatus } : item));
      toast.success(`Order status updated to ${newStatus}`);
    } catch {
      toast.error('Failed to update order status');
    }
  };

  const orderStatusBadge = (st, salesID) => {
    const val = st || 'PENDING';
    const colors = {
      PROCESSING: { bg: '#e0e7ff', text: '#3730a3', border: '#c7d2fe' },
      PACKED: { bg: '#ffedd5', text: '#9a3412', border: '#fed7aa' },
      SHIPPED: { bg: '#f3e8ff', text: '#6b21a8', border: '#e9d5ff' },
      DELIVERED: { bg: '#dcfce7', text: '#166534', border: '#bbf7d0' },
      RETURNED: { bg: '#fef3c7', text: '#92400e', border: '#fde68a' },
      CANCELLED: { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
      PENDING: { bg: '#f1f5f9', text: '#475569', border: '#cbd5e1' }
    };
    const style = colors[val] || colors.PENDING;
    return (
      <select
        value={val}
        onClick={e => e.stopPropagation()}
        onChange={e => handleOrderStatusQuickChange(salesID, e.target.value)}
        style={{
          padding: '3px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700,
          border: `1px solid ${style.border}`, background: style.bg, color: style.text,
          cursor: 'pointer', outline: 'none', appearance: 'auto'
        }}
      >
        {ORDER_STATUSES.map(s => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>
    );
  };

  const columns = [
    {
      header: 'Invoice No', key: 'invoiceNo', render: r => {
        const skus = (invItems[r.salesID]?.rows || []).map(it => it.skuNo).filter(Boolean);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <code
              style={{ fontSize: 12, color: 'var(--primary-600)', cursor: 'pointer', textDecoration: 'underline', fontWeight: 600 }}
              onClick={() => openEdit(r)}
            >
              {r.invoiceNo}
            </code>
            {skus.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {skus.map(s => (
                  <span key={s} style={{ fontSize: 10, fontWeight: 700, background: 'var(--primary-50,#eff6ff)', color: 'var(--primary-600)', padding: '1px 5px', borderRadius: 4, fontFamily: 'var(--mono)', letterSpacing: '.02em' }}>{s}</span>
                ))}
              </div>
            )}
          </div>
        );
      }
    },
    { header: 'Date', key: 'invoiceDate', render: r => new Date(r.invoiceDate).toLocaleDateString('en-IN') },
    {
      header: 'Customer', key: 'customerName', render: r => (
        <span
          style={{ fontWeight: 600, color: 'var(--primary-600)', cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => openEdit(r)}
        >
          {r.customerName}
        </span>
      )
    },
    { header: 'Channel', key: 'salesChannel', render: r => <span className="text-muted" style={{ fontSize: 12, fontWeight: 500 }}>{r.salesChannel || 'STORE'}</span> },
    {
      header: 'Weight (g)', key: 'totalWeight', align: 'right', render: r => <span className="text-mono">{fmt3(r.totalWeight)}</span>,
      total: rows => fmt3(rows.reduce((s, r) => s + Number(r.totalWeight || 0), 0))
    },
    {
      header: 'Amount', key: 'totalAmount', align: 'right', render: r => <span className="text-mono" style={{ fontWeight: 600 }}>₹{fmt2(r.totalAmount)}</span>,
      total: rows => `₹${fmt2(rows.reduce((s, r) => s + Number(r.totalAmount || 0), 0))}`
    },
    {
      header: 'Paid', key: 'paidAmount', align: 'right', render: r => <span className="text-mono" style={{ color: 'var(--green)' }}>₹{fmt2(r.paidAmount)}</span>,
      total: rows => <span style={{ color: '#4ade80' }}>₹{fmt2(rows.reduce((s, r) => s + Number(r.paidAmount || 0), 0))}</span>
    },
    {
      header: 'Balance', key: 'balanceAmount', align: 'right', render: r => (
        <span className="text-mono" style={{ fontWeight: 600, color: r.balanceAmount > 0 ? 'var(--red)' : 'var(--green)' }}>
          ₹{fmt2(r.balanceAmount)}
        </span>
      ),
      total: rows => {
        const t = rows.reduce((s, r) => s + Number(r.balanceAmount || 0), 0);
        return <span style={{ color: t > 0 ? '#f87171' : '#4ade80' }}>₹{fmt2(t)}</span>;
      }
    },
    {
      header: 'Pay Status', key: 'status', align: 'center', render: r => {
        const isReceived = r.paymentStatus === 'RECEIVED' || r.status === 'PAID' || Number(r.balanceAmount || 0) <= 0.50;
        return statusBadge(isReceived ? 'RECEIVED' : (r.paymentStatus || r.status || 'UNPAID'));
      }
    },
    {
      header: 'Order Status', key: 'orderStatus', align: 'center', render: r => orderStatusBadge(r.orderStatus || r.orderstatus, r.salesID)
    },
    {
      header: 'Actions', key: '_actions', align: 'center', render: r => (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
          <button className="btn btn-ghost btn-sm btn-icon" title="Print Tax Invoice"
            style={{ color: 'var(--navy-700)' }}
            onClick={() => printSalesInvoice(r)}>
            <Printer size={14} />
          </button>
          <button className="btn btn-ghost btn-sm btn-icon" title="Edit Invoice" onClick={() => openEdit(r)}>
            <Edit2 size={14} />
          </button>
          <button className="btn btn-ghost btn-sm btn-icon" title="Delete Invoice"
            style={{ color: 'var(--red)' }}
            onClick={() => setConfirmDelete({ id: r.salesID, no: r.invoiceNo })}>
            <Trash2 size={14} />
          </button>
        </div>
      )
    },
  ];

  const itemColumns = [
    {
      header: 'Date', key: 'invoiceDate', render: r => (
        <span style={{ whiteSpace: 'nowrap', color: 'var(--silver-500)', fontSize: 13 }}>
          {new Date(r.invoiceDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
        </span>
      )
    },
    { header: 'Invoice No', key: 'invoiceNo', render: r => <code style={{ fontSize: 12 }}>{r.invoiceNo}</code> },
    {
      header: 'SKU No', key: 'skuNo', render: r => r.skuNo ? (
        <span style={{ fontFamily: 'var(--mono)', fontSize: 12, fontWeight: 700, color: '#3730a3', background: '#eef2ff', border: '1px solid #c7d2fe', padding: '3px 8px', borderRadius: 6, whiteSpace: 'nowrap' }}>
          {r.skuNo}
        </span>
      ) : <span className="text-muted">—</span>
    },
    {
      header: 'Item', key: 'itemName', render: r => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.itemName}</div>
          <div style={{ fontSize: 11, color: 'var(--silver-500)' }}>{r.customerName}</div>
        </div>
      )
    },
    {
      header: 'Qty', key: 'quantity', align: 'center', render: r => <span className="badge badge-navy">{Number(r.quantity)}</span>,
      total: rows => rows.reduce((s, r) => s + Number(r.quantity || 0), 0)
    },
    {
      header: 'Weight (g)', key: 'weight', align: 'right', render: r => <span className="text-mono">{fmt3(r.weight)}</span>,
      total: rows => fmt3(rows.reduce((s, r) => s + Number(r.weight || 0), 0))
    },
    {
      header: 'Taxable', key: 'taxableAmount', align: 'right', render: r => <span className="text-mono">₹{fmt2(r.taxableAmount)}</span>,
      total: rows => `₹${fmt2(rows.reduce((s, r) => s + Number(r.taxableAmount || 0), 0))}`
    },
    { header: 'GST%', key: 'gstPercent', align: 'right', render: r => <span className="text-mono" style={{ color: 'var(--silver-500)' }}>{Number(r.gstPercent || 0).toFixed(1)}%</span> },
    {
      header: 'Total', key: 'totalAmount', align: 'right', render: r => <span className="text-mono" style={{ fontWeight: 700, color: '#0f766e' }}>₹{fmt2(r.totalAmount)}</span>,
      total: rows => <span style={{ color: '#4ade80' }}>₹{fmt2(rows.reduce((s, r) => s + Number(r.totalAmount || 0), 0))}</span>
    },
    { header: 'Payment Status', key: 'paymentStatus', align: 'center', render: r => statusBadge(r.paymentStatus) },
    {
      header: 'Actions', key: '_actions', align: 'center', render: r => (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }} onClick={e => e.stopPropagation()}>
          <button className="btn btn-ghost btn-sm btn-icon" title="Print Tax Invoice"
            style={{ color: 'var(--navy-700)' }}
            onClick={() => printSalesInvoice({ salesID: r.salesID })}>
            <Printer size={14} />
          </button>
          <button className="btn btn-ghost btn-sm btn-icon" title="Edit Invoice"
            onClick={() => { const row = data.find(x => x.salesID === r.salesID); if (row) openEdit(row); }}>
            <Edit2 size={14} />
          </button>
        </div>
      )
    },
  ];

  const getEntryParty = (r) => {
    if (r.supplierName) return r.supplierName;
    if (r.customerName) return r.customerName;
    if (r.description && r.description.startsWith('Party: ')) {
      const parts = r.description.split(' | ');
      return parts[0].replace('Party: ', '').trim();
    }
    return null;
  };

  const getEntryDetails = (r) => {
    if (!r.description) return null;
    if (r.description.startsWith('Party: ')) {
      const parts = r.description.split(' | ');
      const rem = parts.slice(1).join(' | ').trim();
      return rem || null;
    }
    return r.description;
  };

  const damageColumns = [
    { header: 'Date', key: 'damageDate', render: r => r.damageDate ? new Date(r.damageDate).toLocaleDateString('en-IN') : '—' },
    { header: 'Damage No', key: 'damageNo', render: r => <span className="text-mono" style={{ fontWeight: 700 }}>{r.damageNo || `DMG-${r.damageID}`}</span> },
    {
      header: 'Item / SKU', key: 'itemName', render: r => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.itemName || 'Item Entry'}</div>
          {r.skuNo && <span style={{ fontSize: 11, color: 'var(--silver-500)' }}>{r.skuNo}</span>}
        </div>
      )
    },
    { header: 'Supplier / Party', key: 'supplierName', render: r => getEntryParty(r) || <span className="text-muted">—</span> },
    { header: 'Weight (g)', key: 'netWeight', align: 'right', render: r => <span className="text-mono">{fmt3(r.netWeight || r.grossWeight)}</span> },
    { header: 'Price / Value', key: 'purchasePrice', align: 'right', render: r => <span className="text-mono" style={{ fontWeight: 700, color: '#dc2626' }}>₹{fmt2(r.purchasePrice)}</span> },
    {
      header: 'Status', key: 'status', align: 'center', render: r => (
        <span className={`badge ${r.status === 'Repaired' ? 'badge-green' : 'badge-red'}`}>
          {r.status || 'Damage'}
        </span>
      )
    },
    { header: 'Remarks / Reason', key: 'description', render: r => getEntryDetails(r) || <span className="text-muted">—</span> },
    {
      header: 'Actions', key: '_actions', align: 'center', render: r => (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
          <button className="btn btn-ghost btn-sm btn-icon" title="Edit Entry"
            style={{ color: 'var(--navy-700)' }}
            onClick={() => openEditDamage(r, 'damage')}>
            <Edit2 size={14} />
          </button>
          <button className="btn btn-ghost btn-sm btn-icon" title="Delete Entry"
            style={{ color: 'var(--red)' }}
            onClick={() => handleDeleteDamage(r)}>
            <Trash2 size={14} />
          </button>
        </div>
      )
    }
  ];

  const exchangeColumns = [
    { header: 'Date', key: 'damageDate', render: r => r.damageDate ? new Date(r.damageDate).toLocaleDateString('en-IN') : '—' },
    { header: 'Exchange No', key: 'damageNo', render: r => <span className="text-mono" style={{ fontWeight: 700 }}>{r.damageNo || `EXCH-${r.damageID}`}</span> },
    {
      header: 'Item / SKU', key: 'itemName', render: r => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.itemName || 'Exchanged Item'}</div>
          {r.skuNo && <span style={{ fontSize: 11, color: 'var(--silver-500)' }}>{r.skuNo}</span>}
        </div>
      )
    },
    { header: 'Customer / Party', key: 'supplierName', render: r => getEntryParty(r) || <span className="text-muted">—</span> },
    { header: 'Exch Weight (g)', key: 'netWeight', align: 'right', render: r => <span className="text-mono">{fmt3(r.netWeight)}</span> },
    { header: 'Exch Value (₹)', key: 'purchasePrice', align: 'right', render: r => <span className="text-mono" style={{ fontWeight: 700, color: '#d97706' }}>₹{fmt2(r.purchasePrice)}</span> },
    {
      header: 'Status', key: 'status', align: 'center', render: r => (
        <span className="badge badge-amber">
          {r.status || 'Exchange'}
        </span>
      )
    },
    { header: 'Details', key: 'description', render: r => getEntryDetails(r) || <span className="text-muted">—</span> },
    {
      header: 'Actions', key: '_actions', align: 'center', render: r => (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
          <button className="btn btn-ghost btn-sm btn-icon" title="Edit Entry"
            style={{ color: 'var(--navy-700)' }}
            onClick={() => openEditDamage(r, 'exchange')}>
            <Edit2 size={14} />
          </button>
          <button className="btn btn-ghost btn-sm btn-icon" title="Delete Entry"
            style={{ color: 'var(--red)' }}
            onClick={() => handleDeleteDamage(r)}>
            <Trash2 size={14} />
          </button>
        </div>
      )
    }
  ];

  const damageEntries = damageList.filter(r => (r.status || '').toLowerCase().includes('damage') || (r.status || '') === 'Repaired' || (r.status || '') === 'Written Off' || !(r.status || '').toLowerCase().includes('exchange'));
  const exchangeEntries = damageList.filter(r => (r.status || '').toLowerCase().includes('exchange'));

  // ── Tree view for Damage / Exchange: full entry detail, no fetch needed
  // (every field already rides on the list row) ──
  const damageDetailPanel = (r) => {
    const cell = (label, value) => (
      <div style={{ padding: '10px 14px' }}>
        <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--silver-500)' }}>{label}</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy-800, #1e293b)', marginTop: 2, overflowWrap: 'anywhere' }}>{value ?? '—'}</div>
      </div>
    );
    const money = (n) => n ? `₹${fmt2(n)}` : '—';
    return (
      <div style={{ padding: '10px 16px 14px 48px' }}>
        <div style={{ background: '#fff', border: '1px solid var(--silver-200, #e2e8f0)', borderRadius: 10, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
          {cell('Item', r.itemName || '—')}
          {cell('SKU No', r.skuNo ? <code style={{ fontSize: 12, fontWeight: 700, color: 'var(--primary)' }}>{r.skuNo}</code> : '—')}
          {cell('Vendor SKU', r.vendorSKUNo || '—')}
          {cell('Supplier / Party', r.supplierName || '—')}
          {cell('Branch', r.branchName || '—')}
          {cell('Gross Wt', r.grossWeight ? `${fmt3(r.grossWeight)} g` : '—')}
          {cell('Net Wt', r.netWeight ? `${fmt3(r.netWeight)} g` : '—')}
          {cell('Labour/g', money(r.labourPerGram))}
          {cell('Silver/g', money(r.silverRatePerGram))}
          {cell('Purchase Price', <span style={{ color: '#dc2626', fontWeight: 700 }}>{money(r.purchasePrice)}</span>)}
          {cell('Status', r.status || '—')}
          {cell('Entered On', r.createdOn ? new Date(r.createdOn).toLocaleDateString('en-IN') : '—')}
        </div>
        {r.description && (
          <div style={{ marginTop: 8, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 14px', fontSize: 12.5, color: '#78350f' }}>
            <b>Remarks:</b> {r.description}
          </div>
        )}
      </div>
    );
  };

  // ── Item View grouping (None / Invoice / Customer / Item) ─────
  const GROUP_LABEL = { invoice: 'Invoice', customer: 'Customer', item: 'Item' };
  const groupKeyOf = (r) => groupBy === 'invoice' ? r.invoiceNo
    : groupBy === 'customer' ? r.customerName
      : groupBy === 'item' ? (r.skuNo || r.itemName)
        : null;
  const groupLabelOf = (r) => groupBy === 'item' ? `${r.skuNo || '—'} — ${r.itemName}` : groupKeyOf(r);

  const filteredItemRows = itemRows.filter(r => {
    if (!itemSearch.trim()) return true;
    const q = itemSearch.toLowerCase();
    return [r.invoiceNo, r.customerName, r.skuNo, r.itemName].some(v => String(v || '').toLowerCase().includes(q));
  });

  const groupedRows = groupBy === 'none' ? null : (() => {
    const map = new Map();
    filteredItemRows.forEach(r => {
      const key = groupKeyOf(r) || '—';
      if (!map.has(key)) map.set(key, { key, label: groupLabelOf(r) || '—', rows: [] });
      map.get(key).rows.push(r);
    });
    return Array.from(map.values()).map(g => ({
      ...g,
      count: g.rows.length,
      totalWeight: g.rows.reduce((s, r) => s + Number(r.weight || 0), 0),
      totalAmount: g.rows.reduce((s, r) => s + Number(r.totalAmount || 0), 0),
    }));
  })();

  // th style for the items table
  const th = (align = 'right', w) => ({
    padding: '7px 6px', fontSize: 10, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.04em',
    color: 'var(--silver-500)', textAlign: align, whiteSpace: 'nowrap', width: w
  });

  return (
    <div>
      <div className="flex-between mb-6">
        <div>
          <h1>Sales Invoices</h1>
          <p className="text-muted">
            {view === 'invoices' ? `${data.length} invoices`
              : view === 'items' ? `${itemRows.length} item lines`
                : view === 'damage' ? `${damageEntries.length} damage entries`
                  : `${exchangeEntries.length} exchange entries`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {/* View toggle — 4 options */}
          <div style={{ display: 'flex', border: '1px solid var(--silver-300)', borderRadius: 8, overflow: 'hidden' }}>
            {[
              ['invoices', 'Invoices'],
              ['items', 'Item View'],
              ['damage', 'Damage'],
              ['exchange', 'Exchange']
            ].map(([k, label]) => (
              <button key={k} type="button" onClick={() => setView(k)}
                style={{
                  padding: '7px 14px', fontSize: 12.5, fontWeight: 600, border: 'none', cursor: 'pointer',
                  background: view === k ? 'var(--navy-900)' : '#fff',
                  color: view === k ? '#fff' : 'var(--silver-500)', transition: 'all .15s'
                }}>
                {label}
              </button>
            ))}
          </div>
          {view === 'items' && (
            <select value={groupBy} onChange={e => setGroupBy(e.target.value)}
              className="form-control" style={{ padding: '7px 10px', fontSize: 12.5, width: 'auto' }}
              title="Group the item list">
              <option value="none">No grouping</option>
              <option value="invoice">Group by Invoice</option>
              <option value="customer">Group by Customer</option>
              <option value="item">Group by Item</option>
            </select>
          )}
          {view === 'damage' ? (
            <button className="btn btn-danger" onClick={() => {
              setDamageForm({
                branchID: branches[0]?.branchID || '', itemID: '', vendorSKUNo: '', supplierID: '',
                damageDate: today(), grossWeight: '', netWeight: '', labourPerGram: '',
                silverRatePerGram: '', purchasePrice: '', description: '', customerName: '', status: 'Damage',
                imageUrl: ''
              });
              setDamageModal('damage');
            }}>
              <Plus size={16} /> New Damage Entry
            </button>
          ) : view === 'exchange' ? (
            <button className="btn btn-amber" onClick={() => {
              setDamageForm({
                branchID: branches[0]?.branchID || '', itemID: '', vendorSKUNo: '', supplierID: '',
                damageDate: today(), grossWeight: '', netWeight: '', labourPerGram: '',
                silverRatePerGram: '', purchasePrice: '', description: '', customerName: '', status: 'Exchange',
                imageUrl: ''
              });
              setDamageModal('exchange');
            }}>
              <Plus size={16} /> New Exchange Entry
            </button>
          ) : (
            <button className="btn btn-amber" onClick={openModal}>
              <Plus size={16} /> New Invoice
            </button>
          )}
        </div>
      </div>

      <div className="card">
        <div style={{ padding: '16px 20px' }}>
          {view === 'invoices' ? (
            <DataTable
              data={rowsWithSku} columns={columns} loading={loading}
              searchKeys={['invoiceNo', 'customerName', 'status', 'orderStatus', 'salesChannel', 'trackingNo', 'courierPartner', '_skuHints']}
              emptyIcon={<TrendingUp size={36} />}
              emptyText="No sales invoices yet — create your first one"
              rowKey={r => r.salesID}
              onExpand={loadInvItems}
              expandedRender={invItemsPanel}
              actions={
                <button type="button" className="btn btn-ghost" onClick={exportSalesDetail}
                  title="Download detailed sales report (one row per item)">
                  <FileSpreadsheet size={15} /> Export Excel
                </button>
              }
            />
          ) : view === 'damage' ? (
            <DataTable
              data={damageEntries} columns={damageColumns} loading={loading}
              searchKeys={['damageNo', 'itemName', 'skuNo', 'description', 'status']}
              emptyIcon={<AlertCircle size={36} />}
              emptyText="No damage entries recorded yet"
              rowKey={r => r.damageID}
              expandedRender={damageDetailPanel}
              exportName="damage-entries"
            />
          ) : view === 'exchange' ? (
            <DataTable
              data={exchangeEntries} columns={exchangeColumns} loading={loading}
              searchKeys={['damageNo', 'itemName', 'skuNo', 'description', 'supplierName']}
              emptyIcon={<TrendingUp size={36} />}
              emptyText="No exchange entries recorded yet"
              rowKey={r => r.damageID}
              expandedRender={damageDetailPanel}
              exportName="exchange-entries"
            />
          ) : groupBy === 'none' ? (
            <DataTable
              data={itemRows} columns={itemColumns} loading={loading}
              searchKeys={['invoiceNo', 'customerName', 'skuNo', 'itemName']}
              emptyIcon={<TrendingUp size={36} />}
              emptyText="No sales items yet"
              exportName="sales-items"
            />
          ) : (
            <div>
              <div className="search-box mb-4">
                <input className="form-control" placeholder={`Search within ${GROUP_LABEL[groupBy].toLowerCase()} groups...`}
                  value={itemSearch} onChange={e => setItemSearch(e.target.value)} />
              </div>
              {loading ? (
                <div className="page-loader" style={{ height: 120 }}><div className="spinner" /><span>Loading...</span></div>
              ) : groupedRows.length === 0 ? (
                <div className="empty-state"><TrendingUp size={36} /><h4>No sales items yet</h4></div>
              ) : groupedRows.map(g => {
                const collapsed = !!collapsedGroups[g.key];
                return (
                  <div key={g.key} style={{ border: '1px solid var(--silver-200)', borderRadius: 10, marginBottom: 10, overflow: 'hidden' }}>
                    <div
                      onClick={() => setCollapsedGroups(prev => ({ ...prev, [g.key]: !collapsed }))}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '10px 14px', background: 'var(--silver-100)', cursor: 'pointer'
                      }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 11, color: 'var(--silver-500)', transform: collapsed ? 'rotate(-90deg)' : 'none', transition: 'transform .15s', display: 'inline-block' }}>▾</span>
                        <span style={{ fontWeight: 700, fontSize: 13.5 }}>{g.label}</span>
                        <span className="badge badge-navy">{g.count} item{g.count !== 1 ? 's' : ''}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 16, fontSize: 12.5 }}>
                        <span className="text-mono text-muted">{fmt3(g.totalWeight)} g</span>
                        <span className="text-mono" style={{ fontWeight: 700, color: '#0f766e' }}>₹{fmt2(g.totalAmount)}</span>
                      </div>
                    </div>
                    {!collapsed && (
                      <DataTable
                        data={g.rows} columns={itemColumns} loading={false}
                        searchKeys={[]} pageSize={g.rows.length || 1} hideSearch
                        emptyIcon={<TrendingUp size={36} />} emptyText="No items"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Create / Edit Invoice Modal ── */}
      {(modal === 'new' || modal === 'edit') && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal" style={{ maxWidth: 1080, width: '97vw', maxHeight: '93vh', overflowY: 'auto' }}>

            {/* Sticky header */}
            <div className="modal-header" style={{
              position: 'sticky', top: 0, background: '#fff', zIndex: 10,
              borderRadius: '24px 24px 0 0', borderBottom: '1px solid var(--silver-200)'
            }}>
              <div>
                <h3 style={{ margin: 0 }}>{editID ? 'Edit Sales Invoice' : 'New Sales Invoice'}</h3>
                <p style={{ fontSize: 12, color: 'var(--silver-500)', margin: 0 }}>
                  {editID
                    ? `Editing ${data.find(d => d.salesID === editID)?.invoiceNo || '#' + editID}`
                    : 'Invoice No auto-generated · Selling price is GST-inclusive · Stock auto-deducted'}
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {submitted && (headerErrCount + detailErrCount) > 0 && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '4px 10px', borderRadius: 20,
                    background: 'var(--red-light)', color: 'var(--red)',
                    fontSize: 12, fontWeight: 600
                  }}>
                    <AlertCircle size={13} />
                    {headerErrCount + detailErrCount} error{headerErrCount + detailErrCount > 1 ? 's' : ''}
                  </div>
                )}
                {submitted && isFormValid && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '4px 10px', borderRadius: 20,
                    background: 'var(--green-light)', color: '#065f46',
                    fontSize: 12, fontWeight: 600
                  }}>
                    <CheckCircle size={13} /> All valid
                  </div>
                )}
                <button className="btn btn-ghost btn-icon" onClick={() => setModal(false)}>
                  <X size={18} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSave} noValidate>
              <div className="modal-body" style={{ paddingTop: 16 }}>

                {/* ── Row 1: Invoice / Customer ── */}
                <div style={{
                  background: submitted && headerErrCount > 0 ? '#fff5f5' : 'var(--silver-100)',
                  border: `1px solid ${submitted && headerErrCount > 0 ? 'var(--red)' : 'var(--silver-200)'}`,
                  borderRadius: 'var(--radius)', padding: '12px 14px', marginBottom: 12
                }}>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <Field label="Invoice Date" required error={hErr.invoiceDate} style={{ flex: '0 0 135px' }}>
                      <input className="form-control" type="date" max={today()}
                        value={header.invoiceDate}
                        style={{ padding: '6px 8px', fontSize: 13, borderColor: hErr.invoiceDate ? 'var(--red)' : undefined }}
                        onChange={e => setH('invoiceDate', e.target.value)} />
                    </Field>
                    <Field label="Branch" required error={hErr.branchID} style={{ flex: '0 0 120px' }}>
                      <SearchableSelect
                        value={header.branchID}
                        onChange={val => setH('branchID', val)}
                        options={branches.map(b => ({ value: b.branchID, label: b.branchName }))}
                        placeholder="Branch"
                        error={hErr.branchID}
                      />
                    </Field>
                    <Field label="Customer Name" required error={hErr.customerID} style={{ flex: '1 1 220px', minWidth: 200 }}
                      labelExtra={
                        <button
                          type="button"
                          onClick={() => setQuickAddCust(true)}
                          title="Add New Customer"
                          style={{ background: 'none', border: 'none', color: 'var(--primary-600)', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '0 2px' }}
                        >
                          <PlusCircle size={13} />
                        </button>
                      }>
                      <SearchableSelect
                        value={header.customerID}
                        onChange={val => {
                          const cust = customers.find(c => c.customerID === Number(val));
                          const addr = cust ? [cust.address, cust.city, cust.state, cust.pinCode].filter(Boolean).join(', ') : '';
                          const branch = branches.find(b => String(b.branchID) === String(header.branchID));
                          const bState = (branch?.state || 'Gujarat').toLowerCase().trim();
                          const cState = (cust?.state || '').toLowerCase().trim();
                          const isInterState = Boolean(cState && bState && cState !== bState);

                          setHeader(h => ({
                            ...h,
                            customerID: val,
                            billingAddress: addr || h.billingAddress,
                            shippingAddress: (h.sameAsBilling !== false) ? (addr || h.billingAddress) : h.shippingAddress
                          }));
                          if (isInterState) {
                            setDetails(d => d.map(r => ({ ...r, isIGST: true })));
                          }
                        }}
                        options={customers.map(c => ({ value: c.customerID, label: `${c.customerName}${c.mobile ? ` — ${c.mobile}` : ''}` }))}
                        placeholder="— Select customer —"
                        error={hErr.customerID}
                      />
                    </Field>
                    <Field label="Contact No" style={{ flex: '0 0 110px' }}>
                      <ROBox value={selCustomer?.mobile || '—'} color="var(--navy-700)" />
                    </Field>
                    <Field label="City" style={{ flex: '0 0 100px' }}>
                      <ROBox value={selCustomer?.city || '—'} color="var(--navy-700)" />
                    </Field>
                    <Field label="State" style={{ flex: '0 0 110px' }}>
                      <ROBox value={selCustomer?.state || '—'} color="var(--navy-700)" />
                    </Field>
                    <Field label="Sales Channel" style={{ flex: '0 0 130px' }}>
                      <SearchableSelect
                        value={header.salesChannel}
                        onChange={val => setH('salesChannel', val)}
                        options={CHANNELS.map(c => ({ value: c, label: c }))}
                      />
                    </Field>
                  </div>
                </div>

                {/* ── Row 2: Payment / Fulfilment ── */}
                <div style={{
                  background: 'var(--silver-100)', border: '1px solid var(--silver-200)',
                  borderRadius: 'var(--radius)', padding: '12px 14px', marginBottom: 12
                }}>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <Field label="Payment Method" style={{ flex: '0 0 130px' }}>
                      <SearchableSelect
                        value={header.paymentMode}
                        onChange={val => setH('paymentMode', val)}
                        options={[...new Set([header.paymentMode, ...PAY_METHODS])].filter(Boolean)
                          .map(m => ({ value: m, label: m }))}
                      />
                    </Field>
                    <Field label="Payment Status" style={{ flex: '0 0 120px' }}>
                      <SearchableSelect
                        value={header.paymentStatus}
                        onChange={val => {
                          setH('paymentStatus', val);
                          if (val === 'RECEIVED') {
                            setH('paidAmount', totals.total);
                          } else if (val === 'PENDING') {
                            setH('paidAmount', 0);
                          }
                        }}
                        options={PAY_STATUSES.map(s => ({
                          value: s,
                          label: s,
                          color: s === 'RECEIVED' ? '#166534' : s === 'PENDING' ? '#ef4444' : '#b45309'
                        }))}
                      />
                    </Field>
                    {header.paymentStatus === 'PARTIAL' && (
                      <Field label="Amount Received ₹" required error={hErr.paidAmount} style={{ flex: '0 0 130px' }}>
                        <NumInput
                          value={header.paidAmount}
                          placeholder="e.g. 5000"
                          error={!!hErr.paidAmount}
                          onChange={v => setH('paidAmount', typeof v === 'object' && v !== null ? (v?.target?.value ?? '') : v)}
                        />
                      </Field>
                    )}
                    <Field label="Order Status" style={{ flex: '0 0 130px' }}>
                      <SearchableSelect
                        value={header.orderStatus}
                        onChange={val => setH('orderStatus', val)}
                        options={ORDER_STATUSES.map(s => ({ value: s, label: s }))}
                      />
                    </Field>
                    <Field label="Courier Partner" style={{ flex: '0 0 140px' }}>
                      <input className="form-control" list="courier-options" placeholder="SHIPROCKET"
                        style={{ padding: '6px 8px', fontSize: 13, textTransform: 'uppercase' }}
                        value={header.courierPartner}
                        onChange={e => setH('courierPartner', e.target.value.toUpperCase())} />
                      <datalist id="courier-options">
                        {COURIERS.map(c => <option key={c} value={c} />)}
                      </datalist>
                    </Field>
                    <Field label="Tracking No" style={{ flex: '0 0 135px' }}>
                      <input className="form-control" placeholder="DHSGS222445"
                        style={{ padding: '6px 8px', fontSize: 13, fontFamily: 'var(--mono)' }}
                        value={header.trackingNo}
                        onChange={e => setH('trackingNo', e.target.value)} />
                    </Field>
                    <Field label="Shipping (₹)" style={{ flex: '0 0 110px' }}>
                      <input type="number" step="0.01" className="form-control" placeholder="0.00"
                        style={{ padding: '6px 8px', fontSize: 13, fontFamily: 'var(--mono)', textAlign: 'right' }}
                        value={header.shippingCharges}
                        onChange={e => setH('shippingCharges', e.target.value)} />
                    </Field>
                    <Field label="Notes" style={{ flex: '1 1 140px', minWidth: 120 }}>
                      <input className="form-control" placeholder="XYZ"
                        style={{ padding: '6px 8px', fontSize: 13 }}
                        value={header.remarks}
                        onChange={e => setH('remarks', e.target.value)} />
                    </Field>
                  </div>
                </div>

                {/* ── Addresses: Billing & Shipping Collapsible Dropdown ── */}
                <div style={{
                  background: 'var(--silver-100)', border: '1px solid var(--silver-200)',
                  borderRadius: 'var(--radius)', marginBottom: 14, overflow: 'hidden'
                }}>
                  <div
                    onClick={() => setShowAddressSection(v => !v)}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 14px', cursor: 'pointer', userSelect: 'none',
                      background: showAddressSection ? '#e2e8f0' : 'var(--silver-100)',
                      transition: 'background 0.15s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {showAddressSection ? <ChevronUp size={16} color="var(--navy-900)" /> : <ChevronDown size={16} color="var(--navy-900)" />}
                      <span style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--navy-900)' }}>
                        Billing & Shipping Address
                      </span>
                      {header.billingAddress && !showAddressSection && (
                        <span style={{ fontSize: 11, color: 'var(--silver-600)', fontStyle: 'italic', marginLeft: 8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 300 }}>
                          ({header.billingAddress})
                        </span>
                      )}
                    </div>

                    <div onClick={e => e.stopPropagation()}>
                      <label style={{ fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, color: 'var(--silver-600)' }}>
                        <input type="checkbox" checked={header.sameAsBilling !== false}
                          onChange={e => {
                            const checked = e.target.checked;
                            setHeader(h => ({
                              ...h,
                              sameAsBilling: checked,
                              shippingAddress: checked ? h.billingAddress : h.shippingAddress
                            }));
                          }} />
                        Shipping address same as Billing address
                      </label>
                    </div>
                  </div>

                  {showAddressSection && (
                    <div style={{ padding: '12px 14px', borderTop: '1px solid var(--silver-200)', background: '#fff' }}>
                      <div className="grid grid-2" style={{ gap: 12 }}>
                        <Field label="Billing Address">
                          <textarea className="form-control" rows={2} style={{ fontSize: 12, resize: 'none' }}
                            placeholder="Billing Address..." value={header.billingAddress || ''}
                            onChange={e => {
                              const val = e.target.value;
                              setHeader(h => ({
                                ...h,
                                billingAddress: val,
                                shippingAddress: (h.sameAsBilling !== false) ? val : h.shippingAddress
                              }));
                            }} />
                        </Field>

                        <Field label="Shipping Address">
                          <textarea className="form-control" rows={2} style={{ fontSize: 12, resize: 'none' }}
                            placeholder="Shipping Address..." value={header.shippingAddress || ''}
                            disabled={header.sameAsBilling !== false}
                            onChange={e => setH('shippingAddress', e.target.value)} />
                        </Field>
                      </div>
                    </div>
                  )}
                </div>

                {/* ── Items — one row per SKU, Excel style ── */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <h4 style={{ margin: 0 }}>Items</h4>
                    {submitted && detailErrCount > 0 && (
                      <span style={{
                        fontSize: 11, color: 'var(--red)', fontWeight: 600,
                        background: 'var(--red-light)', padding: '2px 8px', borderRadius: 10,
                        display: 'flex', alignItems: 'center', gap: 3
                      }}>
                        <AlertCircle size={10} /> {detailErrCount} error{detailErrCount > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={addLine}>
                    <PlusCircle size={14} /> Add Row
                  </button>
                </div>

                <div style={{
                  border: `1px solid ${submitted && detailErrCount > 0 ? 'var(--red)' : 'var(--silver-200)'}`,
                  borderRadius: 'var(--radius)', overflow: 'visible', marginBottom: 16
                }}>
                  <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed' }}>
                    <thead>
                      <tr style={{ background: 'var(--silver-100)', borderBottom: '1px solid var(--silver-200)' }}>
                        <th style={th('center', 28)}>#</th>
                        <th style={th('left')}>SKU No / Item *</th>
                        <th style={th('center', 52)}>Qty</th>
                        <th style={th('right', 74)}>Net g *</th>
                        <th style={th('right', 76)}>MRP</th>
                        <th style={th('right', 68)}>Disc.</th>
                        <th style={th('right', 90)}>Selling ₹ *</th>
                        <th style={th('right', 90)}>Taxable 🔒</th>
                        <th style={th('center', 48)}>GST %</th>
                        <th style={th('right', 84)}>GST ₹ 🔒</th>
                        <th style={th('center', 36)}>IG</th>
                        <th style={th('right', 92)}>Total 🔒</th>
                        <th style={th('center', 32)}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {details.map((d, i) => {
                        const e = dErrs[i] || {};
                        const c = lineCalc(d);
                        const hasErr = Object.keys(e).length > 0;
                        // Edit-loaded lines have itemID but no skuKey — resolve to the first matching option
                        const selKey = d.skuKey || skuOptions.find(o => o.itemID === +d.itemID)?.key || '';
                        const selOpt = skuOptions.find(o => o.key === selKey);
                        return (
                          <tr key={i} style={{
                            borderBottom: i < details.length - 1 ? '1px solid var(--silver-200)' : 'none',
                            background: hasErr && submitted ? '#fff8f8' : 'transparent'
                          }}>
                            <td style={{ padding: '6px 4px', textAlign: 'center', fontSize: 10, fontWeight: 800, color: 'var(--silver-500)' }}>{i + 1}</td>
                            <td style={{ padding: '6px 5px' }}>
                              <SearchableSelect
                                value={selKey}
                                onChange={val => onItemSelect(i, val)}
                                options={skuOptions.map(o => ({ value: o.key, label: o.label }))}
                                placeholder="— SKU / Item —"
                                error={e.itemID && submitted}
                              />
                              {selOpt?.skuNo && (
                                <div style={{ fontSize: 10, fontFamily: 'var(--mono)', color: '#3730a3', marginTop: 2 }}>
                                  SKU: {selOpt.skuNo}
                                </div>
                              )}
                              {d.itemID && (() => {
                                const avail = availableQtyMap[d.itemID] != null ? availableQtyMap[d.itemID] : 0;
                                return (
                                  <div style={{ fontSize: 10, fontFamily: 'var(--mono)', marginTop: 2, color: avail > 0 ? 'var(--green)' : 'var(--red)' }}>
                                    In stock: {avail}
                                  </div>
                                );
                              })()}
                            </td>
                            <td style={{ padding: '6px 5px' }}>
                              <NumInput value={d.quantity} step="1" placeholder="1"
                                error={!!(e.quantity && submitted)}
                                style={{ textAlign: 'center' }}
                                onChange={ev => onQtyChange(i, ev.target.value)} />
                              {submitted && <Err msg={e.quantity} />}
                            </td>
                            <td style={{ padding: '6px 5px' }}>
                              <NumInput value={d.weight} step="0.001" placeholder="0.000"
                                error={!!(e.weight && submitted)}
                                onChange={ev => onWeightChange(i, ev.target.value)} />
                            </td>
                            <td style={{ padding: '6px 5px' }}>
                              <NumInput value={d.mrp} step="0.01" placeholder="0.00"
                                onChange={ev => onMrpChange(i, ev.target.value)} />
                            </td>
                            <td style={{ padding: '6px 5px' }}>
                              <NumInput value={d.discount} step="0.01" placeholder="0"
                                onChange={ev => onDiscountChange(i, ev.target.value)} />
                            </td>
                            <td style={{ padding: '6px 5px' }}>
                              <NumInput value={d.sellingPrice} step="0.01" placeholder="5000.00"
                                error={!!(e.sellingPrice && submitted)}
                                style={{ fontWeight: 700 }}
                                onChange={ev => onPriceChange(i, ev.target.value)} />
                            </td>
                            <td style={{ padding: '6px 5px' }}>
                              <ROBox value={c.sp > 0 ? fmt2(c.taxable) : '—'} color={c.sp > 0 ? 'var(--navy-700)' : '#9ca3af'} />
                            </td>
                            <td style={{ padding: '6px 5px' }}>
                              <NumInput value={d.gstPercent} step="0.5" placeholder="3"
                                error={!!(e.gstPercent && submitted)}
                                style={{ textAlign: 'center' }}
                                onChange={ev => updateLine(i, 'gstPercent', ev.target.value)} />
                            </td>
                            <td style={{ padding: '6px 5px' }}>
                              <ROBox value={c.sp > 0 ? fmt2(c.gstAmt) : '—'} color={c.sp > 0 ? '#6b21a8' : '#9ca3af'} />
                            </td>
                            <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                              <input type="checkbox" checked={d.isIGST} title="Inter-state (IGST)"
                                onChange={ev => updateLine(i, 'isIGST', ev.target.checked)}
                                style={{ width: 14, height: 14, cursor: 'pointer', accentColor: '#7c3aed' }} />
                            </td>
                            <td style={{ padding: '6px 8px' }}>
                              <ROBox value={`₹${fmt2(c.sp)}`} bold color={c.sp > 0 ? 'var(--navy-800)' : '#9ca3af'} />
                            </td>
                            <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                              {details.length > 1 && (
                                <button type="button"
                                  onClick={() => removeLine(i)}
                                  style={{
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    color: 'var(--silver-400)', padding: 4, borderRadius: 4,
                                    display: 'flex', alignItems: 'center'
                                  }}
                                  onMouseEnter={e2 => e2.currentTarget.style.color = 'var(--red)'}
                                  onMouseLeave={e2 => e2.currentTarget.style.color = 'var(--silver-400)'}>
                                  <Trash2 size={14} />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* ── Totals bar ── */}
                <div style={{
                  background: 'var(--navy-900)', borderRadius: 10, padding: '10px 16px',
                  display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between'
                }}>
                  <div style={{ color: 'var(--silver-400)', fontSize: 12, fontWeight: 600 }}>
                    {details.length} item{details.length !== 1 ? 's' : ''}
                    &nbsp;·&nbsp; Payment:&nbsp;
                    <span style={{ color: header.paymentStatus === 'RECEIVED' ? '#6ee7b7' : header.paymentStatus === 'PENDING' ? '#fca5a5' : '#fcd34d', fontWeight: 700 }}>
                      {header.paymentStatus}
                    </span>
                    {header.paymentStatus === 'PARTIAL' && (
                      <span> &nbsp;·&nbsp; Paid: <span style={{ color: '#6ee7b7' }}>₹{fmt2(header.paidAmount)}</span> &nbsp;·&nbsp; Balance: <span style={{ color: '#fca5a5' }}>₹{fmt2(Math.max(0, totals.total - Number(header.paidAmount || 0)))}</span></span>
                    )}
                    {header.paymentStatus !== 'PARTIAL' && balance > 0 && <span> &nbsp;·&nbsp; Balance: <span style={{ color: '#fca5a5' }}>₹{fmt2(balance)}</span></span>}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', color: 'var(--silver-400)', fontSize: 11 }}>
                    <span>Taxable: <span style={{ color: '#fff' }}>₹{fmt2(totals.taxable)}</span></span>
                    <span>CGST: <span style={{ color: '#a5f3fc' }}>₹{fmt2(totals.cgst)}</span></span>
                    <span>SGST: <span style={{ color: '#a5f3fc' }}>₹{fmt2(totals.sgst)}</span></span>
                    {totals.igst > 0 && <span>IGST: <span style={{ color: '#a5f3fc' }}>₹{fmt2(totals.igst)}</span></span>}
                    {Number(header.discount) > 0 && <span>Disc: <span style={{ color: '#fca5a5' }}>− ₹{fmt2(header.discount)}</span></span>}
                    {Number(header.shippingCharges) > 0 && <span>Ship: <span style={{ color: '#6ee7b7' }}>+ ₹{fmt2(header.shippingCharges)}</span></span>}
                    <span style={{ fontFamily: 'var(--mono)', color: 'var(--amber)', fontWeight: 800, fontSize: 17, whiteSpace: 'nowrap' }}>
                      ₹{fmt2(totals.total)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Modal footer */}
              <div className="modal-footer" style={{ position: 'sticky', bottom: 0, background: '#fff', zIndex: 10, borderTop: '1px solid var(--silver-200)', borderRadius: '0 0 24px 24px' }}>
                <button type="button" className="btn btn-ghost" onClick={() => setModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-amber"
                  disabled={saving}
                  style={{ minWidth: 160, justifyContent: 'center', opacity: saving ? 0.8 : 1 }}>
                  {saving
                    ? <><span className="spinner" style={{ width: 15, height: 15, borderTopColor: 'var(--navy-900)' }} /> Saving...</>
                    : <><Save size={15} /> {editID ? 'Update Invoice' : 'Create Invoice'}</>
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Quick Add Customer Dialog ── */}
      {quickAddCust && (
        <div className="modal-overlay" style={{ zIndex: 999999 }} onClick={e => e.target === e.currentTarget && setQuickAddCust(false)}>
          <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ borderBottom: '1px solid var(--silver-200)' }}>
              <h3 style={{ margin: 0, color: 'var(--navy-900)' }}>Add Customer</h3>
              <button type="button" className="btn btn-ghost btn-icon" onClick={() => setQuickAddCust(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleQuickCustSave}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 20, paddingBottom: 20 }}>
                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: 600 }}>Customer Name *</label>
                  <input
                    className="form-control"
                    placeholder="e.g. Brijesh Patel"
                    value={quickCustForm.customerName}
                    onChange={e => setQuickCustForm({ ...quickCustForm, customerName: e.target.value })}
                    required
                    autoFocus
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div className="form-group">
                    <label className="form-label" style={{ fontWeight: 600 }}>Mobile</label>
                    <input className="form-control" placeholder="98XXXXXXXX" value={quickCustForm.mobile}
                      onChange={e => setQuickCustForm({ ...quickCustForm, mobile: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label" style={{ fontWeight: 600 }}>City</label>
                    <input className="form-control" placeholder="e.g. Ahmedabad" value={quickCustForm.city}
                      onChange={e => setQuickCustForm({ ...quickCustForm, city: e.target.value })} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: 600 }}>State</label>
                  <input className="form-control" placeholder="e.g. Gujarat" value={quickCustForm.state}
                    onChange={e => setQuickCustForm({ ...quickCustForm, state: e.target.value })} />
                </div>
                <p style={{ margin: 0, fontSize: 12, color: 'var(--silver-500)' }}>
                  Full details (address, GST, credit limit) can be added later from the Customers page.
                </p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setQuickAddCust(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={quickCustSaving}>
                  {quickCustSaving ? <span className="spinner" style={{ width: 16, height: 16 }} /> : <Save size={15} />} Add Customer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Confirm Delete Dialog ── */}
      {
        confirmDelete && (
          <div className="modal-overlay" onClick={() => setConfirmDelete(null)}>
            <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
              <div className="modal-header" style={{ borderBottom: '1px solid var(--silver-200)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--red-light)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Trash2 size={18} color="var(--red)" />
                  </div>
                  <h3 style={{ margin: 0 }}>Delete Sales Invoice</h3>
                </div>
                <button className="btn btn-ghost btn-icon" onClick={() => setConfirmDelete(null)}>
                  <X size={18} />
                </button>
              </div>
              <div className="modal-body" style={{ paddingTop: 20, paddingBottom: 20 }}>
                <p style={{ fontSize: 15, color: 'var(--navy-700)', lineHeight: 1.6 }}>
                  Are you sure you want to delete invoice{' '}
                  <strong style={{ color: 'var(--navy-900)' }}>{confirmDelete.no}</strong>?
                </p>
                <p style={{ fontSize: 13, color: 'var(--red)', marginTop: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <AlertCircle size={14} />
                  Stock entries will be reversed and payment records removed. This cannot be undone.
                </p>
              </div>
              <div className="modal-footer" style={{ borderTop: '1px solid var(--silver-200)' }}>
                <button className="btn btn-ghost" onClick={() => setConfirmDelete(null)}>Cancel</button>
                <button className="btn btn-danger" onClick={handleDelete}
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Trash2 size={15} /> Delete Invoice
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* ── Damage / Exchange Entry Modal ── */}
      {damageModal && (
        <div className="modal-overlay" onClick={() => setDamageModal(false)}>
          <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{damageModal === 'exchange' ? 'Add Exchange Entry' : 'Add Damage Stock Entry'}</h3>
              <button className="btn btn-ghost btn-icon" onClick={() => setDamageModal(false)}>
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSaveDamage}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className="grid grid-2" style={{ gap: 14 }}>
                  <div>
                    <label className="form-label required">Date</label>
                    <input type="date" className="form-control" value={damageForm.damageDate}
                      onChange={e => setDamageForm({ ...damageForm, damageDate: e.target.value })} required />
                  </div>

                  <div>
                    <label className="form-label required">Branch</label>
                    <select className="form-control" value={damageForm.branchID}
                      onChange={e => setDamageForm({ ...damageForm, branchID: e.target.value })} required>
                      <option value="">Select Branch</option>
                      {branches.map(b => <option key={b.branchID} value={b.branchID}>{b.branchName}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="form-label">Item / SKU</label>
                  <SearchableSelect
                    options={skuOptions}
                    value={damageForm.itemID ? (skuOptions.find(o => String(o.itemID) === String(damageForm.itemID) || String(o.key) === String(damageForm.itemID))?.key || damageForm.itemID) : ''}
                    onChange={optVal => {
                      if (optVal) {
                        const selected = skuOptions.find(o => String(o.key) === String(optVal) || String(o.itemID) === String(optVal) || String(o.value) === String(optVal));
                        setDamageForm(f => ({
                          ...f,
                          itemID: selected ? selected.itemID : optVal,
                          vendorSKUNo: selected ? (selected.skuNo || '') : '',
                          netWeight: selected && selected.defWeight ? selected.defWeight : f.netWeight,
                          purchasePrice: selected && selected.defPrice ? selected.defPrice : f.purchasePrice,
                          // The item's own purchase supplier — auto-selected
                          supplierID: selected?.supplierID || ''
                        }));
                      } else {
                        setDamageForm(f => ({ ...f, itemID: '', vendorSKUNo: '', supplierID: '' }));
                      }
                    }}
                    placeholder="Search by SKU or Item Name..."
                  />
                </div>

                {damageModal === 'exchange' ? (
                  <div>
                    <label className="form-label">Customer / Party Name</label>
                    <input className="form-control" placeholder="Enter customer or party name"
                      value={damageForm.customerName || ''}
                      onChange={e => setDamageForm({ ...damageForm, customerName: e.target.value })} />
                  </div>
                ) : (
                  <div className="grid grid-2" style={{ gap: 14 }}>
                    <div>
                      <label className="form-label">Vendor SKU / Ref</label>
                      <input className="form-control" placeholder="Vendor SKU No"
                        value={damageForm.vendorSKUNo}
                        onChange={e => setDamageForm({ ...damageForm, vendorSKUNo: e.target.value })} />
                    </div>

                    <div>
                      <label className="form-label">Supplier</label>
                      {(() => {
                        // Only the selected item's own purchase supplier is offered;
                        // without an item selection (or for item-master fallback rows)
                        // all suppliers remain available.
                        const selSku = skuOptions.find(o => String(o.itemID) === String(damageForm.itemID) || String(o.key) === String(damageForm.itemID));
                        const supOpts = selSku?.supplierID
                          ? suppliers.filter(s => String(s.supplierID) === String(selSku.supplierID))
                          : suppliers;
                        return (
                          <select className="form-control" value={damageForm.supplierID}
                            onChange={e => setDamageForm({ ...damageForm, supplierID: e.target.value })}>
                            <option value="">Select Supplier (Optional)</option>
                            {supOpts.map(s => <option key={s.supplierID} value={s.supplierID}>{s.supplierName}</option>)}
                          </select>
                        );
                      })()}
                    </div>
                  </div>
                )}

                <div className="grid grid-2" style={{ gap: 14 }}>
                  <div>
                    <label className="form-label required">Net Weight (g)</label>
                    <input type="number" step="0.001" className="form-control" placeholder="0.000"
                      value={damageForm.netWeight}
                      onChange={e => setDamageForm({ ...damageForm, netWeight: e.target.value })} required />
                  </div>

                  <div>
                    <label className="form-label">Gross Weight (g)</label>
                    <input type="number" step="0.001" className="form-control" placeholder="0.000"
                      value={damageForm.grossWeight}
                      onChange={e => setDamageForm({ ...damageForm, grossWeight: e.target.value })} />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
                  <div>
                    <label className="form-label">{damageModal === 'exchange' ? 'Exchange Value (₹)' : 'Value / Price (₹)'}</label>
                    <input type="number" step="0.01" className="form-control" placeholder="0.00"
                      value={damageForm.purchasePrice}
                      onChange={e => setDamageForm({ ...damageForm, purchasePrice: e.target.value })} />
                  </div>

                  <div>
                    <label className="form-label">Labour / Repair (₹/g)</label>
                    <input type="number" step="0.01" className="form-control" placeholder="0.00"
                      value={damageForm.labourPerGram}
                      onChange={e => setDamageForm({ ...damageForm, labourPerGram: e.target.value })} />
                  </div>

                  <div>
                    <label className="form-label">Silver Rate (₹/g)</label>
                    <input type="number" step="0.01" className="form-control" placeholder="0.00"
                      value={damageForm.silverRatePerGram}
                      onChange={e => setDamageForm({ ...damageForm, silverRatePerGram: e.target.value })} />
                  </div>
                </div>

                <div>
                  <label className="form-label required">Status</label>
                  <select className="form-control" value={damageForm.status || (damageModal === 'exchange' ? 'Exchange' : 'Damage')}
                    onChange={e => setDamageForm({ ...damageForm, status: e.target.value })} required>
                    {damageModal === 'exchange' ? (
                      <>
                        <option value="Exchange">Exchange</option>
                        <option value="Exchange Pending">Exchange Pending</option>
                        <option value="Completed">Completed</option>
                      </>
                    ) : (
                      <>
                        <option value="Damage">Damage</option>
                        <option value="Repaired">Repaired</option>
                        <option value="Written Off">Written Off</option>
                      </>
                    )}
                  </select>
                </div>

                <div>
                  <label className="form-label">Remarks / Description</label>
                  <textarea className="form-control" rows="2"
                    placeholder={damageModal === 'exchange' ? 'Enter exchange details or reason...' : 'Enter damage reason or notes...'}
                    value={damageForm.description}
                    onChange={e => setDamageForm({ ...damageForm, description: e.target.value })} />
                </div>

                <div>
                  <label className="form-label">{damageModal === 'exchange' ? 'Exchange Item Photo (Optional)' : 'Damage Item Photo (Optional)'}</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {damageForm.imageUrl ? (
                      <div style={{ position: 'relative', width: 72, height: 72, borderRadius: 8, border: '1px solid var(--silver-300)', overflow: 'hidden', flexShrink: 0 }}>
                        <img src={damageForm.imageUrl} alt="Item Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <button type="button" onClick={() => setDamageForm({ ...damageForm, imageUrl: '' })}
                          style={{ position: 'absolute', top: 3, right: 3, background: 'rgba(0,0,0,0.65)', color: '#fff', border: 'none', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                          <X size={12} />
                        </button>
                      </div>
                    ) : (
                      <label style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: '100%', height: 60, border: '2px dashed var(--silver-300)', borderRadius: 8,
                        background: 'var(--silver-100)', cursor: 'pointer', color: 'var(--silver-600)', fontSize: 13, gap: 8,
                        transition: 'all 0.15s'
                      }}>
                        <Camera size={18} color="var(--navy-700)" />
                        <span style={{ fontWeight: 500 }}>Click to attach / upload photo</span>
                        <input type="file" accept="image/*" style={{ display: 'none' }}
                          onChange={e => {
                            const file = e.target.files && e.target.files[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onload = ev => setDamageForm(f => ({ ...f, imageUrl: ev.target.result }));
                              reader.readAsDataURL(file);
                            }
                          }} />
                      </label>
                    )}
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setDamageModal(false)}>Cancel</button>
                <button type="submit" className={`btn ${damageModal === 'exchange' ? 'btn-amber' : 'btn-danger'}`} disabled={savingDamage}>
                  {savingDamage ? 'Saving...' : damageModal === 'exchange' ? 'Save Exchange Entry' : 'Save Damage Entry'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type=number] { -moz-appearance: textfield; }
      `}</style>
    </div>
  );
}
